import React, { useState, useEffect, useMemo, useRef, useCallback, Suspense, lazy } from 'react';
import { ThemeProvider, createTheme, CssBaseline,
  Box, AppBar, Toolbar, Typography, Drawer,
  List, ListItemIcon, ListItemText, Container, Grid, Card, CardContent, CardMedia,
  CardActions, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, FormControl, InputLabel, Select, MenuItem, Paper, IconButton, Badge, Divider,
  Snackbar, Alert, ListItemButton, Tabs, Tab, Pagination,
  Switch, FormControlLabel, Stack, Popover, Checkbox, CircularProgress, Collapse
 } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';

import {
  Dashboard as DashboardIcon, CalendarToday as CalendarIcon,
  Inventory as ProductsIcon, Description as TechnicalSheetIcon,
  People as ClientsIcon, RequestQuote as QuotesIcon,
  Assignment as ServiceOrderIcon, LocalShipping as WarehouseOrderIcon,
  AccountBalance as AccountingIcon, Storage as InventoryIcon,
  SmartToy as AIAssistantIcon, Assessment as ReportsIcon, Psychology as PsychologyIcon,
  Add as AddIcon, Edit as EditIcon, Search as SearchIcon, Refresh as RefreshIcon,
  Notifications as NotificationsIcon, Settings as SettingsIcon,
  Logout as LogoutIcon, Close as CloseIcon, PhotoCamera as PhotoIcon,
  AttachFile as AttachIcon, VideoLibrary as VideoIcon, Save as SaveIcon, Launch as LaunchIcon,
  PictureAsPdf as PdfIcon, Send as SendIcon,
  // === AQUÍ ESTÁN LOS NUEVOS QUE FALTABAN ===
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Timeline as TimelineIcon,
  GroupAdd as GroupAddIcon,
  PostAdd as PostAddIcon,
  WhatsApp as WhatsAppIcon,
  ManageAccounts as ManageAccountsIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  StarRate as StarIcon,
  // === Íconos para menú con grupos ===
  Warning as WarningIcon,
  Store as StoreIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  Image as ImageIcon,
  Campaign as CampaignIcon,
  Web as WebIcon,
  Share as ShareIcon,
  HowToReg as HowToRegIcon,
  Headset as HeadsetIcon,
  PersonAdd as PersonAddIcon,
  DirectionsCar as DirectionsCarIcon,
  Description as DescriptionIcon,
  Tune as TuneIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';

import MenuIcon from '@mui/icons-material/Menu';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

import productosData from './productos-alquiler.v_final.json';
import logoDasilva from './assets/logo.png';
import './App.css';

/** ===== Servicios que ya tienes ===== */
import { downloadQuotePDF as exportQuotePDF } from './services/pdfQuote';
import { estimateTransportCost } from './services/transport';
import { migrateSavedQuotes, assignIdToQuote, deleteById } from './services/savedQuotes';

import AIAssistant from './components/AIAssistant';
import LoginPage from './components/LoginPage';
import EmployeeRegistrationPage from './components/EmployeeRegistrationPage';
import QuotePublicView from './components/QuotePublicView';
import QuoteAction from './components/QuoteAction';

import ContractIcon from '@mui/icons-material/Description';
import PaymentPortal from './components/PaymentPortal';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TechnicalSheetsSection from './components/TechnicalSheetsSection';
import { EmployeeDashboard } from './modules/StaffModule';
import AIUnifiedSection from './components/AIUnifiedSection';
import { placeholderImg, BRAND_LOGO_URL, coverOf, techPhotoOf, noDownload, imgProtectSx, getYouTubeEmbedUrl, urlToDataURL, genQRUrl, PAYMENT_QR_URL, weservProxy, loadImgDataURL, geocodeForMap, theme, drawerWidth } from './utils/helpers';
const QuotesModuleNuevo = lazy(() => import('./modules/QuotesModuleNuevo'));
const BodegaModule = lazy(() => import('./modules/BodegaModule'));
const AIManagement = lazy(() => import('./modules/AIManagement'));
const ClientsModuleComplete = lazy(() => import('./modules/ClientsModule'));
const AccountingModule = lazy(() => import('./modules/AccountingModule'));
const StaffModule = lazy(() => import('./modules/StaffModule'));
const InventoryModule = lazy(() => import('./modules/InventoryModule'));
const UsersModule = lazy(() => import('./modules/UsersModule'));
const EmailMarketingModule = lazy(() => import('./modules/EmailMarketingModule'));
const SurveyModule = lazy(() => import('./modules/SurveyModule'));
const SurveyPublicPage = lazy(() => import('./SurveyPublicPage'));
const AIKnowledgeModule = lazy(() => import('./modules/AIKnowledgeModule'));
const AICommandCenter = lazy(() => import('./modules/AICommandCenter'));
const VentasModule = lazy(() => import('./modules/VentasModule'));
// === Módulos reconectados ===
const AlertsCenterModule = lazy(() => import('./modules/AlertsCenterModule'));
const BazarModule = lazy(() => import('./modules/BazarModule'));
const VouchersModule = lazy(() => import('./modules/VouchersModule'));
const ImagenesCatalogoModule = lazy(() => import('./modules/ImagenesCatalogoModule'));
const MarketingModule = lazy(() => import('./modules/MarketingModule'));
const LandingPagesModule = lazy(() => import('./modules/LandingPagesModule'));
const SEOInspectorModule = lazy(() => import('./modules/SEOInspectorModule'));
const WhatsappBroadcastModule = lazy(() => import('./modules/WhatsappBroadcastModule'));
const LeadsModule = lazy(() => import('./modules/LeadsModule'));
const ConsultorVentasModule = lazy(() => import('./modules/ConsultorVentasModule'));
const CandidatesModule = lazy(() => import('./modules/CandidatesModule'));
const VehicleModule = lazy(() => import('./modules/VehicleModule'));
const LegalDocsModule = lazy(() => import('./modules/LegalDocsModule'));
const BusinessConfigModule = lazy(() => import('./modules/BusinessConfigModule'));
const SystemHealthModule = lazy(() => import('./modules/SystemHealthModule'));
const ContratosModule = lazy(() => import('./modules/ContratosModule'));
const ConversacionesModule = lazy(() => import('./modules/ConversacionesModule'));
const ReportsModule = lazy(() => import('./modules/ReportsModule'));


// SmartToyIcon ya importado como AIAssistantIcon en línea 17



/** ===== App principal ===== */
export default function App() {
  // ── AUTENTICACIÓN ─────────────────────────────────────────
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('token') || '');
  const [authUser,  setAuthUser]  = useState(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  });

  const handleLogin = (token, user) => {
    setAuthToken(token);
    setAuthUser(user);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setAuthToken('');
    setAuthUser(null);
  };

  // 1. DETECTAR MODO CLIENTE (LA PUERTA SECRETA)
  const searchParams = new URLSearchParams(window.location.search);
  const isClientMode = searchParams.get('mode') === 'client';
  const publicQuoteNumber = searchParams.get('quote') || searchParams.get('quoteNumber');
  const isQuotePublicView = !!publicQuoteNumber;
  // 2. DETECTAR RUTA DE ACCIÓN: /quote/:quoteNumber/:action (accept/modify)
  const pathMatch = window.location.pathname.match(/^\/quote\/([^/]+)\/(accept|modify)$/);
  const isQuoteAction = !!pathMatch;
  // 3. DETECTAR RUTA PÚBLICA DE REGISTRO DE EMPLEADO
  const isEmployeeRegistration = window.location.pathname === '/registro-empleado';
  // 4. DETECTAR PÁGINA PÚBLICA DE ENCUESTA POST-EVENTO
  const isSurveyPage = window.location.pathname.startsWith('/encuesta/');
  // 5. DETECTAR PORTAL DE PAGOS [Task 9]
  const isPaymentPage = window.location.pathname === '/pagar';
  // isPublicRoute se usa más abajo al final (después de todos los hooks)
  const isPublicRoute = isQuotePublicView || isQuoteAction || isClientMode || isEmployeeRegistration || isSurveyPage || isPaymentPage;

  // --- ESTADO DEL FORMULARIO (CEREBRO 9.0 - COMPLETO) ---
  const [newEventForm, setNewEventForm] = useState({
    id: null,
    title: '', 
    type: 'evento', 
    date: '', 
    start: '10:00', 
    end: '14:00', 
    location: '', 
    clientName: '', 
    coordinatorName: '',   // <--- NUEVO
    coordinatorPhone: '',  // <--- NUEVO
    balanceDue: '',        // <--- NUEVO
    notes: '',
    equipment: [],
    km: 0,
    tieneCoordinador: true,
    coordinadorVirtualNombre: '',
    coordinadorVirtualTelefono: '',
    observacionesEntrega: '',
    instructionTemplateId: ''
  });

  // --- ESTADOS PARA REPORTE PDF POR RANGO Y TIPO (CEREBRO 14.0) ---
  const [openPdfDialog, setOpenPdfDialog] = useState(false);
  const [pdfReportType, setPdfReportType] = useState('all'); // 'all', 'events', 'logistics'
  const [pdfDateRange, setPdfDateRange] = useState({
    start: new Date().toISOString().slice(0, 10), 
    end: new Date(new Date().setDate(new Date().getDate() + 7)).toISOString().slice(0, 10) 
  });

  // --- ESTADO PARA FILTRO VISUAL DEL CALENDARIO ---
  const [calendarFilter, setCalendarFilter] = useState('all'); // 'all', 'evento', 'montaje', 'transporte'

  // --- PLANTILLAS DE INSTRUCCIONES (Fase B) ---
  const [instructionTemplates, setInstructionTemplates] = useState([]);

  // 2. TUS VARIABLES ORIGINALES (LAS QUE BORRASTE ANTES)
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuGroups, setMenuGroups] = useState({});
  const toggleGroup = (id) => setMenuGroups(prev => ({ ...prev, [id]: !prev[id] }));
  const [activeSection, setActiveSection] = useState('dashboard');
  const [productsTab, setProductsTab] = useState(0); // 0=Alquiler 1=Arcade 2=Inflables 3=Playground
  const [notifAnchor, setNotifAnchor] = useState(null);

/** ===== Productos ===== */
  // 1. DEFINICIÓN DE VARIABLES (ESTO ES LO QUE FALTA)
  const [products, setProducts] = useState([]);

// 2. CARGA PRODUCTOS — Google Sheets CSV (fuente única) con fallback a JSON local
  const mapProductData = (source) => (source || []).map((p, i) => ({
    uid: String(p.codigo || p.id || i),
    ...p,
    stock: Number(p.stock) || 1,
    stock_mantenimiento: Number(p.stock_mantenimiento) || 0,
    stock_baja: Number(p.stock_baja) || 0,
    images: Array.isArray(p.images) ? [...p.images] : (p.link_foto ? [p.link_foto] : []),
    coverIndex: Number.isInteger(p.coverIndex) ? p.coverIndex : 0,
    documents: Array.isArray(p.documents) ? [...p.documents] : [],
    videos: Array.isArray(p.videos) ? [...p.videos] : [],
    techSheet: {
      marca: p.marca || 'Da Silva',
      material: p.material || 'Lona reforzada / PVC',
      area_requerida: p.area_requerida || '',
      capacidad_ninos: p.capacidad_ninos || '',
      capacidad_adultos: p.capacidad_adultos || '',
      peso_equipo: p.peso_equipo || '',
      voltaje: p.voltaje || '110V',
      potencia: p.potencia || '',
      blower: p.blower || '',
      tiempo_armado: p.tiempo_armado || '20–40 min',
      anclajes: p.anclajes || '',
      terreno: p.terreno || '',
      viento_max: p.viento_max || ''
    }
  }));
  useEffect(() => {
    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';
    // 1) Cargar productos desde Google Sheets CSV
    fetch(`${apiBase}/products/catalog-csv`)
      .then(r => r.json())
      .then(d => {
        const rawProducts = d.ok && d.products?.length ? d.products : productosData;
        console.log(`[Productos] ${rawProducts.length} cargados` + (d.ok && d.products?.length ? ' desde Google Sheets' : ' desde JSON local'));
        // 2) Cargar fichas técnicas desde MongoDB y fusionar
        fetch(`${apiBase}/tech-sheets/public`)
          .then(r2 => r2.json())
          .then(tsData => {
            const sheets = tsData.ok ? tsData.data : {};
            const merged = rawProducts.map((p, i) => {
              const ts = sheets[p.codigo] || {};
              return {
                uid: String(p.codigo || p.id || i),
                ...p,
                stock: Number(p.stock) || 1,
                stock_mantenimiento: Number(p.stock_mantenimiento) || 0,
                stock_baja: Number(p.stock_baja) || 0,
                images: Array.isArray(p.images) ? [...p.images] : (p.link_foto ? [p.link_foto] : []),
                coverIndex: Number.isInteger(p.coverIndex) ? p.coverIndex : 0,
                documents: Array.isArray(p.documents) ? [...p.documents] : [],
                videos: Array.isArray(p.videos) ? [...p.videos] : [],
                // Campos directos del tech sheet para el diálogo de detalle
                peso_equipo: ts.peso_equipo || p.peso_equipo || '',
                voltaje: ts.voltaje || p.voltaje || '110V',
                amperaje: ts.amperaje || p.amperaje || '',
                potencia: ts.potencia_w || p.potencia || '',
                tipo_inflador: ts.tipo_inflador || p.tipo_inflador || '',
                capacidad_max_personas: ts.capacidad_max_personas || p.capacidad_max_personas || '',
                peso_max_usuario: ts.peso_max_usuario || p.peso_max_usuario || '',
                altura_max_usuario: ts.altura_max_usuario || p.altura_max_usuario || '',
                tipo_anclaje: ts.tipo_anclaje || p.tipo_anclaje || '',
                area_requerida: ts.area_requerida || p.area_requerida || '',
                viento_max: ts.viento_max || p.viento_max || '',
                temp_operacion: ts.temp_operacion || p.temp_operacion || '',
                restricciones: ts.restricciones || p.restricciones || '',
                tiempo_armado: ts.tiempo_armado || p.tiempo_armado || '20–40 min',
                operarios_requeridos: ts.operarios_requeridos || p.operarios || '',
                norma_tecnica: ts.norma_tecnica || '',
                vida_util: ts.vida_util || '',
                material: ts.material || p.material || '',
                tipo_funcionamiento: ts.tipo_funcionamiento || '',
                // Objeto techSheet completo para la sección de Fichas Técnicas
                techSheet: {
                  marca: ts.marca || p.marca || 'Da Silva',
                  fabricante: ts.fabricante || p.fabricante || 'Inflables Da Silva',
                  pais_origen: ts.pais_origen || 'Colombia',
                  fecha_fabricacion: ts.fecha_fabricacion || '',
                  vida_util: ts.vida_util || '',
                  norma_tecnica: ts.norma_tecnica || 'NTC 5743',
                  tipo_funcionamiento: ts.tipo_funcionamiento || '',
                  metodo_operacion: ts.metodo_operacion || '',
                  material: ts.material || 'Lona reforzada / PVC',
                  area_requerida: ts.area_requerida || '',
                  capacidad_ninos: ts.capacidad_max_personas || p.capacidad_max_personas || '',
                  capacidad_adultos: ts.capacidad_max_personas || '',
                  peso_equipo: ts.peso_equipo || p.peso_equipo || '',
                  voltaje: ts.voltaje || '110V',
                  amperaje: ts.amperaje || '',
                  potencia: ts.potencia_w || p.potencia || '',
                  tipo_inflador: ts.tipo_inflador || '',
                  blower: ts.tipo_inflador || '',
                  tiempo_armado: ts.tiempo_armado || '20–40 min',
                  anclajes: ts.tipo_anclaje || '',
                  terreno: ts.terreno || p.terreno || '',
                  viento_max: ts.viento_max || '',
                  temp_operacion: ts.temp_operacion || '',
                  peso_max_usuario: ts.peso_max_usuario || '',
                  altura_max_usuario: ts.altura_max_usuario || '',
                  restricciones: ts.restricciones || '',
                  no_uso_lluvia: ts.no_uso_lluvia ?? true,
                  requiere_electrico: ts.requiere_electrico ?? true
                }
              };
            });
            setProducts(merged);
            console.log(`[Tech Sheets] ${Object.keys(sheets).length} fichas fusionadas desde MongoDB`);
          })
          .catch(e => {
            console.warn('[Tech Sheets] No se pudieron cargar fichas de MongoDB:', e.message);
            setProducts(mapProductData(rawProducts));
          });
      })
      .catch(() => {
        setProducts(mapProductData(productosData));
        console.log('[Productos] Fallback: cargados desde JSON local (sin conexión)');
      });
  }, []);

  // 3. DEFINICIÓN DE PRODUCTOS FILTRADOS (TAMBIÉN FALTABA)
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todas');
  useEffect(() => setFilteredProducts(products), [products]);
  useEffect(() => {
    let list = products;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter(p =>
        (p.nombre || '').toLowerCase().includes(q) ||
        (p.codigo || '').toLowerCase().includes(q) ||
        (p.categoria || '').toLowerCase().includes(q)
      );
    }
    if (selectedCategory !== 'Todas') list = list.filter(p => p.categoria === selectedCategory);
    setFilteredProducts(list);
    setPage(1);
  }, [searchTerm, selectedCategory, products]);
  const categories = useMemo(() => {
    // Solo categorías del catálogo de alquiler (no mezclar con ventas)
    const dynamic = [...new Set(products.map(p => p.categoria).filter(Boolean))].sort();
    return ['Todas', ...dynamic];
  }, [products]);
  const [page, setPage] = useState(1);
  const itemsPerPage = 12;
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const current = filteredProducts.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  /** ===== Vistas de productos ===== */
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editTab, setEditTab] = useState(0);
  const [cardImageIdx, setCardImageIdx] = useState({});
  const [detailImageIdx, setDetailImageIdx] = useState(0);
  const handleViewProduct = (p) => { setSelectedProduct(p); setDetailImageIdx(0); setOpenProductDialog(true); };
  const handleEditProduct = (p) => {
    const clone = JSON.parse(JSON.stringify(p));
    // Normalizar: asegurar que amperaje esté disponible
    if (clone.amperaje_arranque && !clone.amperaje) clone.amperaje = clone.amperaje_arranque;
    setEditingProduct(clone);
    setEditTab(0);
    setOpenEditDialog(true);
  };
  const handleAddProduct = () => {
    const ts = Date.now();
    const autoCode = 'PROD-' + ts.toString(36).toUpperCase();
    const newP = { uid: 'NEW-' + ts, codigo: autoCode, nombre: '', categoria: '', precio_alquiler_5horas: '', jornada_completa: false, horas_jornada: 12, precio_jornada: '', stock: 1, stock_mantenimiento: 0, stock_baja: 0, medidas: '', voltaje: '110V', amperaje: '', potencia: '', operarios: 1, volumen_m3: 0, peso_equipo: '', edades_permitidas: '', capacidad_ninos: '', capacidad_adultos: '', tiempo_armado: '', area_requerida: '', terreno: '', viento_max: '', anclajes: '', observaciones: '', images: [], documents: [], videos: [], coverIndex: 0, techPhotoUrl: '' };
    setEditingProduct(newP); setEditTab(0); setOpenEditDialog(true);
  };
  const handleSaveEdit = async () => {
    if (!editingProduct.codigo || !editingProduct.codigo.trim()) {
      setSnackbar({ open: true, message: 'El código del producto es obligatorio', severity: 'error' });
      return;
    }
    if (!editingProduct.nombre || !editingProduct.nombre.trim()) {
      setSnackbar({ open: true, message: 'El nombre del producto es obligatorio', severity: 'error' });
      return;
    }
    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = 'Bearer ' + token;
    // Mapear campos español→inglés para el backend
    const categoryMap = { 'Inflables': 'castillos', 'Juegos Mecánicos': 'juegos', 'Toboganes': 'toboganes', 'Acuáticos': 'acuaticos', 'Temáticos': 'tematicos', 'Obstáculos': 'obstaculos' };
    const backendBody = {
      name: editingProduct.nombre,
      description: editingProduct.observaciones || editingProduct.nombre,
      category: categoryMap[editingProduct.categoria] || 'juegos',
      price: Number(editingProduct.precio_alquiler_5horas) || 0,
      stock: Number(editingProduct.stock) || 1,
      available: true,
      specifications: {
        dimensions: { length: 0, width: 0, height: 0 },
        capacity: Number(editingProduct.capacidad_ninos) || 1,
        setupTime: Number(editingProduct.tiempo_armado) || 30,
        weight: Number(editingProduct.peso_equipo) || 0,
      },
      // Guardar también los campos originales para que el catálogo los use
      codigo: editingProduct.codigo,
      nombre: editingProduct.nombre,
      categoria: editingProduct.categoria,
      precio_alquiler_5horas: Number(editingProduct.precio_alquiler_5horas) || 0,
      medidas: editingProduct.medidas,
      voltaje: editingProduct.voltaje,
      volumen_m3: Number(editingProduct.volumen_m3) || 0,
      operarios: Number(editingProduct.operarios) || 1,
      jornada_completa: !!editingProduct.jornada_completa,
      horas_jornada: Number(editingProduct.horas_jornada) || 12,
      precio_jornada: Number(editingProduct.precio_jornada) || 0,
    };
    // Producto local para el catálogo del frontend
    const localProduct = { ...editingProduct, uid: editingProduct.uid?.startsWith('NEW-') ? editingProduct.codigo : editingProduct.uid };

    // ⭐ GUARDAR EDICIÓN EN EL CATÁLOGO (fuente unificada): persiste TODOS los campos
    // (incluido volumen) y se refleja tanto en el panel como en José. Por código.
    if (editingProduct.codigo) {
      try {
        await fetch(apiBase + '/products/catalog/' + encodeURIComponent(editingProduct.codigo), {
          method: 'PUT', headers,
          body: JSON.stringify({
            nombre: editingProduct.nombre,
            categoria: editingProduct.categoria,
            precio_alquiler_5horas: Number(editingProduct.precio_alquiler_5horas) || 0,
            medidas: editingProduct.medidas,
            volumen_m3: Number(editingProduct.volumen_m3) || 0,
            edades_permitidas: editingProduct.edades_permitidas,
            operarios: Number(editingProduct.operarios) || 1,
            voltaje: editingProduct.voltaje,
            amperaje: editingProduct.amperaje,
            peso_equipo: editingProduct.peso_equipo,
            capacidad_ninos: editingProduct.capacidad_ninos,
            capacidad_adultos: editingProduct.capacidad_adultos,
            tiempo_armado: editingProduct.tiempo_armado,
            area_requerida: editingProduct.area_requerida,
            observaciones: editingProduct.observaciones,
            // Foto de portada elegida con el botón "Hacer portada"
            link_foto: (Array.isArray(editingProduct.images) && editingProduct.images.length)
              ? (editingProduct.images[editingProduct.coverIndex ?? 0] || editingProduct.images[0])
              : '',
          }),
        });
      } catch (eCat) { console.warn('No se pudo guardar la edición en el catálogo:', eCat.message); }
    }

    try {
      if (editingProduct.uid?.startsWith('NEW-')) {
        const res = await fetch(apiBase + '/products', { method: 'POST', headers, body: JSON.stringify(backendBody) });
        const data = await res.json();
        if (res.ok && data.success) {
          const saved = { ...localProduct, _id: data.data?._id || data.id, uid: data.data?._id || editingProduct.codigo };
          setProducts(prev => [...prev, saved]);
          setSnackbar({ open: true, message: 'Producto creado exitosamente', severity: 'success' });
        } else {
          setProducts(prev => [...prev, localProduct]);
          setSnackbar({ open: true, message: 'Producto guardado localmente (backend: ' + (data.message || 'error') + ')', severity: 'warning' });
        }
      } else if (editingProduct._id) {
        // Producto con _id de MongoDB → actualizar
        const res = await fetch(apiBase + '/products/' + editingProduct._id, { method: 'PUT', headers, body: JSON.stringify(backendBody) });
        if (res.ok) {
          setProducts(prev => prev.map(p => (p.uid === editingProduct.uid || p._id === editingProduct._id) ? { ...editingProduct } : p));
          setSnackbar({ open: true, message: 'Producto actualizado', severity: 'success' });
        } else {
          setProducts(prev => prev.map(p => (p.uid === editingProduct.uid ? { ...editingProduct } : p)));
          setSnackbar({ open: true, message: 'Actualizado localmente (error backend)', severity: 'warning' });
        }
      } else {
        // Producto del catálogo CSV (sin _id) → crear en MongoDB por primera vez
        const res = await fetch(apiBase + '/products', { method: 'POST', headers, body: JSON.stringify(backendBody) });
        const data = await res.json();
        if (res.ok && data.success) {
          const saved = { ...editingProduct, _id: data.data?._id, uid: data.data?._id || editingProduct.codigo };
          setProducts(prev => prev.map(p => p.uid === editingProduct.uid ? saved : p));
          setSnackbar({ open: true, message: 'Producto guardado', severity: 'success' });
        } else {
          setProducts(prev => prev.map(p => p.uid === editingProduct.uid ? { ...editingProduct } : p));
          setSnackbar({ open: true, message: 'Actualizado localmente', severity: 'success' });
        }
      }
    } catch (err) {
      if (editingProduct.uid?.startsWith('NEW-')) {
        setProducts(prev => [...prev, localProduct]);
      } else {
        setProducts(prev => prev.map(p => (p.uid === editingProduct.uid ? { ...editingProduct } : p)));
      }
      setSnackbar({ open: true, message: 'Guardado localmente (sin conexión al backend)', severity: 'warning' });
    }
    setOpenEditDialog(false);
  };
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const addImages = (e) => {
    const files = Array.from(e.target.files || []);
    const urls = files.map(f => URL.createObjectURL(f));
    setEditingProduct(ep => ({ ...ep, images: [...(ep.images || []), ...urls] }));
    e.target.value = '';
  };
  const addImageByUrl = () => {
    const url = imageUrlInput.trim();
    if (!url) return;
    setEditingProduct(ep => ({ ...ep, images: [...(ep.images || []), url] }));
    setImageUrlInput('');
  };
  const removeImage = (idx) => setEditingProduct(ep => {
    const imgs = [...(ep.images || [])]; imgs.splice(idx, 1);
    return { ...ep, images: imgs, coverIndex: Math.min(ep.coverIndex ?? 0, imgs.length - 1), techPhotoUrl: (imgs.includes(ep.techPhotoUrl) ? ep.techPhotoUrl : '') };
  });
  const setCover = (idx) => setEditingProduct(ep => ({ ...ep, coverIndex: idx }));
  const setTechPhoto = (url) => setEditingProduct(ep => ({ ...ep, techPhotoUrl: url }));
  const addDocuments = (e) => {
    const files = Array.from(e.target.files || []).map(f => ({ name: f.name }));
    setEditingProduct(ep => ({ ...ep, documents: [...(ep.documents || []), ...files] }));
  };
  const removeDocument = (i) => setEditingProduct(ep => { const docs = [...(ep.documents || [])]; docs.splice(i, 1); return { ...ep, documents: docs }; });
  const addVideos = (e) => {
    const files = Array.from(e.target.files || []).map(f => ({ name: f.name, url: URL.createObjectURL(f) }));
    setEditingProduct(ep => ({ ...ep, videos: [...(ep.videos || []), ...files] }));
  };
  const addVideoByUrl = () => {
    const url = videoUrlInput.trim();
    if (!url) return;
    const name = url.includes('youtu') ? 'YouTube' : url.split('/').pop()?.substring(0, 30) || 'Video';
    setEditingProduct(ep => ({ ...ep, videos: [...(ep.videos || []), { name, url }] }));
    setVideoUrlInput('');
  };
  const removeVideo = (i) => setEditingProduct(ep => { const vids = [...(ep.videos || [])]; vids.splice(i, 1); return { ...ep, videos: vids }; });

  /** ===== Snackbar ===== */
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  /** ===== Clientes (mejorado) ===== */
  const [clients, setClients] = useState([]);
  
  const defaultClientForm = {
    id:'', type:'personal', name:'', contactPerson:'', document:'', email:'', phone:'', 
    city:'', address:'', postalCode:'', notes:'', hasCredit:false, howFoundUs:'', 
    referredBy:'', otherSource:'',
    billing: {
      companyName:'', tradeName:'', taxId:'', address:'', billingContact:'',
      billingPhone:'', billingEmail:''
    },
    documents: []
  };
  
  const [clientForm, setClientForm] = useState(defaultClientForm);
  const [editingClientId, setEditingClientId] = useState(null);
  const [clientTab, setClientTab] = useState(0);
  const [openClientDialog, setOpenClientDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  const resetClientForm = () => { 
    setClientForm(defaultClientForm); 
    setEditingClientId(null); 
    setClientTab(0);
  };
  
  const handleSaveClient = () => {
    if (!clientForm.name || !clientForm.email) { 
      setSnackbar({ open:true, message:'Nombre y email son obligatorios', severity:'warning' }); 
      return; 
    }
    
    const clientData = {
      ...clientForm,
      billing: {
        ...clientForm.billing,
        // Auto-completar datos de facturación si están vacíos
        companyName: clientForm.billing.companyName || (clientForm.type === 'company' ? clientForm.name : ''),
        taxId: clientForm.billing.taxId || clientForm.document,
        address: clientForm.billing.address || clientForm.address,
        billingContact: clientForm.billing.billingContact || (clientForm.type === 'company' ? clientForm.contactPerson : clientForm.name),
        billingPhone: clientForm.billing.billingPhone || clientForm.phone,
        billingEmail: clientForm.billing.billingEmail || clientForm.email
      }
    };
    
    if (editingClientId) {
      setClients(prev => prev.map(c => (c.id === editingClientId ? { ...clientData, id: editingClientId } : c)));
      setSnackbar({ open:true, message:'Cliente actualizado', severity:'success' });
    } else {
      const newId = `C-${String(clients.length + 1).padStart(3,'0')}`;
      setClients(prev => [{ ...clientData, id:newId }, ...prev]);
      setSnackbar({ open:true, message:'Cliente creado', severity:'success' });
    }
    resetClientForm();
  };
  
  const handleEditClient = (c) => { 
    setEditingClientId(c.id); 
    setClientForm({ 
      ...defaultClientForm, 
      ...c, 
      billing: { ...defaultClientForm.billing, ...(c.billing || {}) },
      documents: c.documents || []
    }); 
    setClientTab(0);
  };
  
  const handleViewClient = (c) => {
    setSelectedClient(c);
    setOpenClientDialog(true);
  };
  
  const handleDeleteClient = (id) => { 
    setClients(prev => prev.filter(c => c.id !== id)); 
    setSnackbar({ open:true, message:'Cliente eliminado', severity:'success' }); 
    if (editingClientId === id) resetClientForm(); 
  };
  
  const addClientDocument = (e, clientId) => {
    const files = Array.from(e.target.files || []);
    const newDocs = files.map(f => ({
      name: f.name,
      type: f.type,
      size: f.size,
      uploadDate: new Date().toISOString(),
      category: 'client-upload'
    }));
    
    if (clientId) {
      // Agregar a cliente existente
      setClients(prev => prev.map(c => 
        c.id === clientId 
          ? { ...c, documents: [...(c.documents || []), ...newDocs] }
          : c
      ));
    } else {
      // Agregar al formulario
      setClientForm(prev => ({
        ...prev,
        documents: [...(prev.documents || []), ...newDocs]
      }));
    }
    
    e.target.value = '';
    setSnackbar({ open:true, message:`${files.length} documento(s) agregado(s)`, severity:'success' });
  };
  
  const removeClientDocument = (index, clientId) => {
    if (clientId) {
      setClients(prev => prev.map(c => 
        c.id === clientId 
          ? { ...c, documents: c.documents.filter((_, i) => i !== index) }
          : c
      ));
    } else {
      setClientForm(prev => ({
        ...prev,
        documents: prev.documents.filter((_, i) => i !== index)
      }));
    }
    setSnackbar({ open:true, message:'Documento eliminado', severity:'success' });
  };

  /** ===== Estados del Calendario ===== */
  // --- CEREBRO 10.0: MEMORIA PERMANENTE (INICIO) ---
  // 1. Intentamos leer la memoria guardada, si no hay nada, cargamos la lista vacía
  const [calendarEvents, setCalendarEvents] = useState(() => {
    try {
      const savedEvents = localStorage.getItem('calendarEvents_v1');
      return savedEvents ? JSON.parse(savedEvents) : []; 
    } catch (error) {
      console.error("Error cargando memoria:", error);
      return [];
    }
  });

  // 2. Efecto para GUARDAR AUTOMÁTICAMENTE cada vez que hagas un cambio
  useEffect(() => {
    try {
      localStorage.setItem('calendarEvents_v1', JSON.stringify(calendarEvents));
    } catch (error) {
      console.error("Error guardando memoria:", error);
    }
  }, [calendarEvents]);
  // --- FIN MEMORIA PERMANENTE ---

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [calendarView, setCalendarView] = useState('month'); // month, week, day
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openEventDialog, setOpenEventDialog] = useState(false);
  const [newEventDialog, setNewEventDialog] = useState(false);
  const [openDayDialog, setOpenDayDialog] = useState(false);
  const [dayDialogDate, setDayDialogDate] = useState(null); // { year, month, day }

  // ══════════════════════════════════════════════════════════════
  // HELPER: Parsear fechas que pueden venir en español o ISO
  // ══════════════════════════════════════════════════════════════
  const parseEventDate = (dateStr) => {
    if (!dateStr) return null;
    // Si ya es ISO (YYYY-MM-DD), retornar directo
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    // Si es ISO con hora
    if (/^\d{4}-\d{2}-\d{2}T/.test(dateStr)) return dateStr.slice(0, 10);
    // Parsear español: "21 de febrero", "15 de marzo de 2026"
    const meses = {
      enero: '01', febrero: '02', marzo: '03', abril: '04', mayo: '05', junio: '06',
      julio: '07', agosto: '08', septiembre: '09', octubre: '10', noviembre: '11', diciembre: '12'
    };
    const match = String(dateStr).match(/(\d{1,2})\s+de\s+(\w+)(?:\s+de\s+(\d{4}))?/i);
    if (match) {
      const dia = match[1].padStart(2, '0');
      const mesNombre = match[2].toLowerCase();
      const mes = meses[mesNombre];
      const anio = match[3] || new Date().getFullYear();
      if (mes) return `${anio}-${mes}-${dia}`;
    }
    // Fallback: intentar Date.parse
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return null;
  };

  // ══════════════════════════════════════════════════════════════
  // CARGAR EVENTOS DESDE MONGODB AL INICIAR (fusionar con localStorage)
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    const fetchCalendarFromDB = async () => {
      try {
        // Forzar limpieza de cache local (corrección zona horaria jun 2026)
        const cacheVer = localStorage.getItem('calendarVersion') || '0';
        if (cacheVer !== 'v3-tz') {
          localStorage.removeItem('calendarEvents');
          localStorage.removeItem('calendarEvents_v1');
          localStorage.setItem('calendarVersion', 'v3-tz');
          console.log('📅 [Calendar] Cache borrado — recargando desde servidor');
        }
        const res = await fetch('/api/calendar');
        if (!res.ok) return;
        const data = await res.json();
        if (data.ok && data.events) {
          setCalendarEvents(prev => {
            const quotesFromDB = new Set(
              data.events.map(ev => ev.quoteNumber).filter(Boolean)
            );
            const filteredPrev = prev.filter(
              ev => !ev.quoteNumber
            );
            const dbMapped = data.events.map(ev => ({
              id: ev.eventId || ev._id,
              title: ev.title,
              type: ev.type || 'evento',
              client: ev.client?.name || '',
              clientPhone: ev.client?.phone || '',
              clientEmail: ev.client?.email || '',
              coordinator: ev.coordinator || null,
              start: ev.start || null,
              end:   ev.end   || null,
              location: ev.location?.address ? `${ev.location.city || ''} - ${ev.location.address}` : (ev.location?.city || ''),
              equipment: ev.equipment?.map(e => e.code || e.name || e) || [],
              equipmentNames: ev.equipment?.map(e => e.name || e.code || '') || [],
              km: ev.km || 0,
              mapsUrl: ev.location?.mapsUrl ||
                (ev.location?.address || ev.location?.city
                  ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${ev.location?.address || ''}, ${ev.location?.city || ''}, Colombia`)}`
                  : ''),
              status: ev.status || 'programado',
              description: ev.description || ev.notes || '',
              quoteNumber: ev.quoteNumber || '',
              orderNumber: ev.orderNumber || '',
              financials: ev.financials || null,
              relatedEvent: ev.parentEventId || null,
              fromDB: true
            }));
            const existingIds = new Set(filteredPrev.map(ev => ev.id || ev.eventId));
            const newNoQuote = dbMapped.filter(
              ev => !ev.quoteNumber && !existingIds.has(ev.id)
            );
            const withQuote = dbMapped.filter(ev => ev.quoteNumber);
            const merged = [...filteredPrev, ...withQuote, ...newNoQuote];
            console.log(`📅 [Calendar] DB: ${data.events.length} eventos. localStorage previo: ${prev.length} → merged: ${merged.length}`);
            return merged;
          });
        }
      } catch (e) {
        console.log('ℹ️ [Calendar] API no disponible, usando solo localStorage');
      }
    };
    fetchCalendarFromDB();
  }, []);

  // Cargar plantillas de instrucciones para selector en formulario de eventos
  useEffect(() => {
    fetch('/api/instructions')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.ok) setInstructionTemplates(d.templates || []); })
      .catch(() => {});
  }, []);

// Función MEJORADA para calcular tiempos automáticamente
  const createCalendarEventsFromOrder = (serviceOrder) => {
    // ══════════════════════════════════════════════════════════════
    // PARSEO ROBUSTO DE FECHA (soporta ISO y español)
    // ══════════════════════════════════════════════════════════════
    const rawDate = serviceOrder.eventDate || serviceOrder.date;
    const eventDate = parseEventDate(rawDate);
    
    if (!eventDate) {
      console.error('❌ [Calendar] No se pudo parsear la fecha:', rawDate);
      return [];
    }

    const startTime = serviceOrder.event?.start || '10:00';
    const endTime = serviceOrder.event?.end || '14:00';

    // Normalizar hora (quitar AM/PM si viene así)
    const normalizeTime = (t) => {
      if (!t) return '10:00';
      // Si ya es HH:MM (24h), retornar
      if (/^\d{1,2}:\d{2}$/.test(t)) return t;
      // Si tiene AM/PM, convertir
      const match = t.match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
      if (match) {
        let h = parseInt(match[1]);
        const m = match[2];
        const period = match[3].toLowerCase();
        if (period === 'pm' && h !== 12) h += 12;
        if (period === 'am' && h === 12) h = 0;
        return `${String(h).padStart(2, '0')}:${m}`;
      }
      return t;
    };

    const start24 = normalizeTime(startTime);
    const end24 = normalizeTime(endTime);

    // ══════════════════════════════════════════════════════════════
    // CÁLCULOS DE TIEMPO (misma fórmula que handleSaveManualEvent)
    // ══════════════════════════════════════════════════════════════
    const toLocalISO = (d) => {
      const pad = n => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    const itemCount = serviceOrder.items?.length || 1;
    const minutesPerGameMontaje = 45;
    const totalMountingMinutes = itemCount * minutesPerGameMontaje;

    const km = serviceOrder.transportDetails?.km || 0;
    const travelTimeMinutes = km > 0 ? (km * 1.5) + 30 : 60;

    // Crear objetos Date
    const eventStartObj = new Date(`${eventDate}T${start24}:00`);
    const eventEndObj = new Date(`${eventDate}T${end24}:00`);

    if (isNaN(eventStartObj.getTime())) {
      console.error('❌ [Calendar] Fecha inválida después de parsear:', eventDate, start24);
      return [];
    }

    // Montaje = Evento - Tiempo de montaje
    const montajeStartObj = new Date(eventStartObj);
    montajeStartObj.setMinutes(montajeStartObj.getMinutes() - totalMountingMinutes);

    // Salida camión = Montaje - Tiempo de viaje
    const departureObj = new Date(montajeStartObj);
    departureObj.setMinutes(departureObj.getMinutes() - travelTimeMinutes);

    // Desmontaje = Fin evento + Tiempo montaje
    const dismantlingEnd = new Date(eventEndObj);
    dismantlingEnd.setMinutes(dismantlingEnd.getMinutes() + totalMountingMinutes);

    // Regreso a bodega = Desmontaje + Tiempo viaje
    const returnArrival = new Date(dismantlingEnd);
    returnArrival.setMinutes(returnArrival.getMinutes() + travelTimeMinutes);

    // ══════════════════════════════════════════════════════════════
    // DATOS DEL CLIENTE Y UBICACIÓN
    // ══════════════════════════════════════════════════════════════
    const clientName = serviceOrder.client?.name || serviceOrder.client || 'Sin nombre';
    const clientPhone = serviceOrder.client?.phone || serviceOrder.client?.telefono || '';
    const clientEmail = serviceOrder.client?.email || '';
    const eventCity = serviceOrder.event?.city || serviceOrder.transportDetails?.city || '';
    const eventAddress = serviceOrder.event?.address || serviceOrder.transportDetails?.address || '';
    const locationStr = eventCity && eventAddress ? `${eventCity} - ${eventAddress}` : (eventCity || eventAddress || 'Sin ubicación');
    
    // Generar URL de Google Maps
    const mapsUrl = eventAddress 
      ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent("Inflables Da Silva, Chía")}&destination=${encodeURIComponent(eventAddress + (eventCity ? ', ' + eventCity : ''))}`
      : '';

    const equipmentCodes = serviceOrder.items?.map(item => item.code) || [];
    const equipmentNames = serviceOrder.items?.map(item => item.name || item.code) || [];
    
    const fmt = (d) => d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: true});
    const mainId = `EV-${Date.now()}`;

    // Coordinador (si se pasa en la orden)
    const coordinatorInfo = serviceOrder.coordinator || {
      name: clientName,
      phone: clientPhone,
      balance: serviceOrder.totals?.total ? (serviceOrder.totals.total - (serviceOrder.totals?.paid || 0)) : 0
    };

    // ══════════════════════════════════════════════════════════════
    // CREAR 3 EVENTOS: Principal + IDA + REGRESO
    // ══════════════════════════════════════════════════════════════

    // 1. EVENTO PRINCIPAL (FIESTA)
    const mainEvent = {
      id: mainId,
      title: clientName,
      type: 'evento',
      client: clientName,
      clientPhone: clientPhone,
      clientEmail: clientEmail,
      coordinator: coordinatorInfo,
      start: toLocalISO(eventStartObj),
      end: toLocalISO(eventEndObj),
      location: locationStr,
      equipment: equipmentCodes,
      equipmentNames: equipmentNames,
      km: km,
      mapsUrl: mapsUrl,
      status: 'confirmado',
      orderServiceId: serviceOrder.id,
      quoteNumber: serviceOrder.quoteNumber || '',
      financials: serviceOrder.totals || null,
      description: `Equipos: ${equipmentNames.join(', ')}\nCliente: ${clientName}\nTeléfono: ${clientPhone}`
    };

    // 2. LOGÍSTICA COMBINADA (Salida bodega → Llegada bodega, con todo el detalle)
    const logisticaDesc = [
      `🚛 Salida bodega: ${fmt(departureObj)}`,
      `🔧 Inicio montaje: ${fmt(montajeStartObj)}`,
      `🎪 Inicio evento: ${fmt(eventStartObj)}`,
      `🏁 Fin evento: ${fmt(eventEndObj)}`,
      `📦 Fin desmontaje: ${fmt(dismantlingEnd)}`,
      `🏠 Llegada bodega: ${fmt(returnArrival)}`,
      km > 0 ? `📏 ${km.toFixed ? km.toFixed(1) : km} km · ${Math.round(travelTimeMinutes)} min de viaje` : '',
      `🎈 Equipos (${itemCount}): ${equipmentNames.join(', ')}`,
    ].filter(Boolean).join('\n');

    const logisticaEvent = {
      id: `LG-${Date.now()}`,
      title: `📋 Logística - ${clientName}`,
      type: 'montaje',
      client: clientName,
      clientPhone: clientPhone,
      clientEmail: clientEmail,
      coordinator: coordinatorInfo,
      start: toLocalISO(departureObj),
      end: toLocalISO(returnArrival),
      location: locationStr,
      equipment: equipmentCodes,
      equipmentNames: equipmentNames,
      km: km,
      mapsUrl: mapsUrl,
      description: logisticaDesc,
      quoteNumber: serviceOrder.quoteNumber || '',
      relatedEvent: mainId
    };

    console.log(`📅 [Calendar] 2 eventos creados para ${eventDate}: ${clientName} (${itemCount} equipos, ${km}km)`);

    // NO guardar en MongoDB desde el frontend — el backend /accept ya lo hace para evitar duplicados
    // Los eventos quedan en localStorage y se sincronizan desde el DB al recargar

    return [mainEvent, logisticaEvent];
  };

// --- FUNCIÓN DE DISPONIBILIDAD CON ESTADOS (Cerebro 16.0) ---
  const checkEquipmentAvailability = (equipmentCodes, startStr, endStr) => {
    const checkStart = new Date(startStr);
    const checkEnd = new Date(endStr);

    // 1. Buscar eventos que se crucen
    const overlappingEvents = calendarEvents.filter(ev => {
      if (ev.status === 'cancelado') return false;
      const evStart = new Date(ev.start);
      const evEnd = new Date(ev.end);
      return (evStart < checkEnd && evEnd > checkStart);
    });

    // 2. Contar ocupación actual
    const usageCounts = {}; 
    overlappingEvents.forEach(ev => {
      if (ev.equipment && Array.isArray(ev.equipment)) {
          ev.equipment.forEach(code => {
              usageCounts[code] = (usageCounts[code] || 0) + 1;
          });
      }
    });

    const conflicts = [];
    const requestedCounts = {};
    equipmentCodes.forEach(code => { requestedCounts[code] = (requestedCounts[code] || 0) + 1; });

    Object.keys(requestedCounts).forEach(code => {
        const product = products.find(p => p.codigo === code || p.uid === code);
        
        // MATEMÁTICA DE STOCK NETO
        const totalFisico = product?.stock || 1;
        const enTaller = product?.stock_mantenimiento || 0;
        const dadosBaja = product?.stock_baja || 0;
        
        // Stock Realmente Disponible = Total - Taller - Baja
        const stockOperativo = Math.max(0, totalFisico - enTaller - dadosBaja);

        const currentlyUsed = usageCounts[code] || 0;
        const needed = requestedCounts[code];

        if (currentlyUsed + needed > stockOperativo) {
            conflicts.push(`${product?.nombre || code} (Disp: ${stockOperativo}, Ocupados: ${currentlyUsed}, Taller/Baja: ${enTaller + dadosBaja})`);
        }
    });

    return {
      available: conflicts.length === 0,
      conflicts,
      conflictingEvents: overlappingEvents
    };
  };

  // Exponer función de verificación de disponibilidad globalmente para las cotizaciones
  useEffect(() => {
    window.checkEquipmentAvailability = checkEquipmentAvailability;
    return () => {
      delete window.checkEquipmentAvailability;
    };
  }, [calendarEvents]);

  /** ===== Órdenes ===== */
  const [serviceOrders, setServiceOrders] = useState([]);
  const [sendingOS, setSendingOS] = useState(null); // quoteNumber enviando
  const [serviceOrdersTab, setServiceOrdersTab] = useState(0); // 0=Próximas, 1=Historial
  const [serviceOrdersSort, setServiceOrdersSort] = useState('fecha_asc');

  const handleResendOSWhatsApp = async (o) => {
    const qn = o.quoteNumber;
    if (!qn) { setSnackbar({ open:true, message:'Esta orden no tiene número de cotización', severity:'warning' }); return; }
    setSendingOS(qn);
    try {
      const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';
      const res = await fetch(`${apiBase}/quotes/${qn}/resend-payment-info`, { method: 'POST' });
      const data = await res.json();
      if (data.ok) setSnackbar({ open:true, message:'✅ OS + datos de pago enviados al cliente por WhatsApp', severity:'success' });
      else setSnackbar({ open:true, message: data.error || 'Error enviando por WhatsApp', severity:'error' });
    } catch(e) {
      setSnackbar({ open:true, message:'Error de conexión', severity:'error' });
    } finally {
      setSendingOS(null);
    }
  };
  const [warehouseOrders, setWarehouseOrders] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editOrderType, setEditOrderType] = useState('');

  const handleEditOrder = (order, type) => {
    setEditingOrder(JSON.parse(JSON.stringify(order)));
    setEditOrderType(type);
  };

  const handleSaveOrder = async () => {
    if (!editingOrder) return;
    try {
      const res = await fetch(`/api/orders/${editOrderType}/${editingOrder.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(editingOrder)
      });
      const data = await res.json();
      if (data.ok) {
        const setter = editOrderType === 'service' ? setServiceOrders : setWarehouseOrders;
        setter(prev => prev.map(o => o.id === editingOrder.id ? (data.item || editingOrder) : o));
        setSnackbar({ open: true, message: 'Orden actualizada', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Error: ' + (data.error || 'desconocido'), severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Error de conexión: ' + err.message, severity: 'error' });
    }
    setEditingOrder(null);
  };

  const allOrders = [...serviceOrders, ...warehouseOrders];

  // Cargar órdenes guardadas desde el backend
  const loadOrders = useCallback(async () => {
    try {
      const [sRes, wRes] = await Promise.all([
        fetch('/api/orders/service', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
        fetch('/api/orders/warehouse', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      ]);
      if (sRes.ok) {
        const sData = await sRes.json();
        if (sData.ok && Array.isArray(sData.items) && sData.items.length > 0) {
          setServiceOrders(sData.items);
        }
      }
      if (wRes.ok) {
        const wData = await wRes.json();
        if (wData.ok && Array.isArray(wData.items) && wData.items.length > 0) {
          setWarehouseOrders(wData.items);
        }
      }
    } catch (e) {
      console.log('ℹ️ [Orders] No se pudieron cargar órdenes del backend');
    }
  }, []);

  // Carga inicial al montar
  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Re-fetch al entrar a las secciones de órdenes (para ver nuevas OS/OB creadas desde cotizaciones)
  useEffect(() => {
    if (activeSection === 'service-orders' || activeSection === 'warehouse-orders') {
      loadOrders();
    }
  }, [activeSection, loadOrders]);

  // Cargar clientes reales desde MongoDB al iniciar y al abrir cotizaciones
  const loadClientsFromBackend = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/clients', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) { console.warn(`[App] /api/clients respondió ${res.status}`); return; }
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.clients || data.items || []);
      if (list.length > 0) {
        const normalized = list.map(c => ({
          ...c,
          id: c.id || c._id?.toString() || c._id
        }));
        setClients(normalized);
        console.log(`✅ [App] ${normalized.length} clientes cargados desde MongoDB`);
      }
    } catch (e) {
      console.log('ℹ️ [App] No se pudieron cargar clientes del backend');
    }
  };

  useEffect(() => { loadClientsFromBackend(); }, []); // carga al inicio

  // Refrescar clientes cada vez que el usuario abre el módulo de cotizaciones
  useEffect(() => {
    if (activeSection === 'cotizaciones' || activeSection === 'quotes') {
      loadClientsFromBackend();
    }
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const companyData = {
    name: 'Inflables Da Silva & FunPark',
    logo: BRAND_LOGO_URL,
    headquarters: [
      { name: 'Sede Principal – Chía', address: 'Calle 1 # 2-3, Chía, Cundinamarca', phone: '+57 333 400 8723', whatsapp: '573334008723' },
      { name: 'Sede Bogotá', address: 'Bogotá, Colombia', phone: '+57 301 000 0000', whatsapp: '573010000000' }
    ],
    email: 'cotizaciones@inflablesdasilva.com',
    website: 'www.inflablesdasilva.com',
    social: {
      instagram: '@inflablesdasilva',
      facebook: 'inflablesdasilva',
      youtube: 'Inflables Da Silva',
      tiktok: '@inflablesdasilva'
    },
    country: 'Colombia',
    paymentTerms: 'Para separar: 50% de anticipo. 50% restante contra entrega (montaje terminado y funcionando).'
  };
  
  const createOrdersFromQuote = (payload) => {
    // ══════════════════════════════════════════════════════════════
    // CORRECCIÓN: Usar fecha del EVENTO, no fecha de CREACIÓN
    // ══════════════════════════════════════════════════════════════
    const rawEventDate = payload.eventDate || payload.event?.date || payload.date || '';
    const eventDateISO = parseEventDate(rawEventDate) || new Date().toISOString().slice(0, 10);
    const creationDate = payload.date || new Date().toISOString().slice(0, 10);
    
    const ymd = eventDateISO.replace(/-/g, '');
    // Consecutivo legible: OS-20260216-001 derivado del número de cotización
    const quoteBase = payload.quoteNumber ? payload.quoteNumber.replace(/^COT-/, '') : `${ymd}-${String(serviceOrders.length + 1).padStart(3,'0')}`;
    const osId = `OS-${quoteBase}`;
    const obId = `OB-${quoteBase}`;

    // Normalizar horas del evento
    const normalizeTime = (t) => {
      if (!t) return '10:00';
      if (/^\d{1,2}:\d{2}$/.test(t)) return t;
      const match = String(t).match(/(\d{1,2}):(\d{2})\s*(am|pm)/i);
      if (match) {
        let h = parseInt(match[1]);
        const m = match[2];
        const period = match[3].toLowerCase();
        if (period === 'pm' && h !== 12) h += 12;
        if (period === 'am' && h === 12) h = 0;
        return `${String(h).padStart(2, '0')}:${m}`;
      }
      return t;
    };

    const scheduleStart = normalizeTime(payload.schedule?.start || '10:00');
    const scheduleEnd = normalizeTime(payload.schedule?.end || '14:00');

    const serviceOrder = {
      id: osId,
      quoteNumber: payload.quoteNumber,
      date: eventDateISO,           // Fecha del EVENTO (ISO)
      eventDate: rawEventDate,       // Fecha original (puede ser español)
      createdDate: creationDate,     // Fecha en que se creó la cotización
      client: payload.client,
      event: { 
        city: payload.transport?.city || '', 
        address: payload.transport?.address || '', 
        start: scheduleStart, 
        end: scheduleEnd 
      },
      items: payload.items.map(it => ({
        code: it.productCode, name: it.productName, qty: it.quantity || 1, hours: it.hours || 0,
        cover: it.cover || coverOf(products.find(p => p.codigo === it.productCode) || {}),
        unitPrice: it.unitPrice || 0,
        lineTotal: it.lineTotal || 0,
      })),
      transportDetails: payload.transport,
      totals: {
        subtotal:    payload.subtotal        || 0,
        ivaPercent:  payload.ivaPercent      || 0,
        iva:         payload.ivaAmount       || 0,
        extraHours:  payload.extraHours      || 0,
        extraCost:   payload.extraCost       || 0,
        transport:   payload.transport?.cost || 0,
        total:       payload.total           || 0,
      },
      company: companyData,
      conditions: payload.conditions || [],
      // Recordatorio de pago — 48h para que el cliente envíe el anticipo
      paymentReminder: {
        active: true,
        deadlineAt: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
        reminder24hSent: false,
        reminder48hSent: false,
        resolved: false,
        clientPhone: (payload.client?.phone || '').replace(/\D/g, ''),
        clientEmail: payload.client?.email || '',
      }
    };

    const obItems = payload.items.map(it => {
      const p = products.find(px => px.codigo === it.productCode);
      const v = Number(p?.volumen_m3 ?? p?.volumen ?? p?.volumenM3 ?? 1) || 1;
      return { code: it.productCode, name: it.productName, qty: it.quantity || 1, volumeM3: v };
    });
    const warehouseOrder = {
      id: obId,
      quoteNumber: payload.quoteNumber,
      serviceOrderId: osId,
      status: 'GENERADA',
      originBranch: payload.originBranch || { name:'Sede Chía', city:'Chía' },
      destination: { city: payload.transport?.city || '', address: payload.transport?.address || '' },
      schedule: { start: scheduleStart, end: scheduleEnd },
      items: obItems,
      notes: 'Despacho según tiempos de orden de servicio.',
      at: new Date().toISOString()
    };

    // Crear eventos de calendario automáticamente
    const newCalendarEvents = createCalendarEventsFromOrder(serviceOrder);
    
    // Actualizar estados
    setServiceOrders(prev => [serviceOrder, ...prev]);
    setWarehouseOrders(prev => [warehouseOrder, ...prev]);
    setCalendarEvents(prev => [...prev, ...newCalendarEvents]);

    setActiveSection('service-orders');
    setSnackbar({ open:true, message:`✅ Orden de Servicio, Bodega y Calendario creados para ${eventDateISO}`, severity:'success' });

    console.log(`📋 [Orders] Creado OS=${osId}, OB=${obId} para fecha=${eventDateISO} (original: ${rawEventDate})`);

    // Guardar en backend (no bloqueante)
    Promise.all([
      fetch('/api/orders/service', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }, body: JSON.stringify(serviceOrder) }),
      fetch('/api/orders/warehouse', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` }, body: JSON.stringify(warehouseOrder) })
    ]).then(() => {
      console.log(`✅ [Orders] OS y OB guardados en backend`);
    }).catch(err => {
      console.warn('⚠️ [Orders] No se pudo guardar en backend:', err.message);
    });

    // Marcar cliente como 'cliente_final' automáticamente al generar una orden
    const clientId = payload.client?._id || payload.client?.id;
    if (clientId) {
      fetch(`/api/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cliente_final' })
      }).then(() => {
        console.log(`✅ [Clients] Cliente ${payload.client?.name} marcado como cliente_final`);
      }).catch(() => {}); // No bloqueante
    }
  };

  /** ===== Export PDF de órdenes ===== */
  const exportServiceOrderPDF = async (order) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const PURPLE = [99, 102, 241];
    const GRAY = [100, 100, 100];
    const fmtCOP = (v) => v ? '$ ' + Number(v).toLocaleString('es-CO') : '—';

    // Normalizar datos (maneja ambos formatos)
    const clientName    = order.client?.name    || order.clientData?.name    || '—';
    const clientEmail   = order.client?.email   || order.clientData?.email   || '—';
    const clientPhone   = order.client?.phone   || order.clientData?.phone   || '—';
    const clientCity    = order.client?.city    || order.clientData?.city    || '';
    const clientCompany = order.client?.company || order.clientData?.company || '';
    const eventCity     = order.event?.city     || order.eventDetails?.city     || '—';
    const eventAddress  = order.event?.address  || order.eventDetails?.address  || '—';
    const evStart       = order.event?.start    || order.eventDetails?.startTime || '—';
    const evEnd         = order.event?.end      || order.eventDetails?.endTime   || '—';
    // Preferir fecha legible ("15 de mayo") sobre ISO ("2026-05-15")
    const eventDate = order.eventDetails?.eventDate || order.eventDate || order.date || '—';

    // ── ENCABEZADO — idéntico a pdfQuote.js ─────────────────────────
    let y = 18;

    // Logo nuevo (JPEG subido por el usuario)
    try {
      const logoData = await loadImgDataURL('https://www.inflablesdasilva.com/wp-content/uploads/2026/02/WhatsApp-Image-2026-02-16-at-6.45.52-AM.jpeg');
      if (logoData) doc.addImage(logoData, 'JPEG', margin, y - 5, 26, 26);
    } catch {}

    // Datos empresa (izquierda) — mismos textos que cotizaciones
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(0, 0, 0);
    doc.text('Inflables Da Silva', margin + 32, y);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text('Dirección: Vereda Fagua sector MG', margin + 32, y + 6);
    doc.text('Tel: +57 3334008723 - 3112952770', margin + 32, y + 12);
    doc.text('Email: contacto@inflablesdasilva.com', margin + 32, y + 18);
    doc.text('Web: www.inflablesdasilva.com | www.dasilvafunpark.com', margin + 32, y + 24);

    // Derecha — ORDEN DE SERVICIO / No. / Fecha
    const rightX = pageWidth - margin;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(...PURPLE);
    doc.text('ORDEN DE SERVICIO', rightX, y, { align: 'right' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(0, 0, 0);
    doc.text(`No.: ${order.id || '—'}`, rightX, y + 6, { align: 'right' });
    const dateStr = new Date().toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.text(`Fecha: ${dateStr}`, rightX, y + 12, { align: 'right' });
    if (order.quoteNumber) doc.text(`Cotización: ${order.quoteNumber}`, rightX, y + 18, { align: 'right' });

    y = y + 30;

    // Línea divisoria discreta (igual que cotizaciones)
    doc.setLineWidth(0.2); doc.setDrawColor(210, 210, 210);
    doc.line(margin + 5, y, pageWidth - margin - 5, y);
    y += 6;

    // ── CLIENTE + EVENTO (dos columnas — igual que cotizaciones) ─────
    const midX = pageWidth / 2;
    const startY = y;

    // Columna izquierda: Cliente
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(0, 0, 0);
    doc.text('Cliente', margin, y); y += 6;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text(`Nombre: ${clientName}`, margin, y); y += 5;
    if (clientCompany && clientCompany !== 'Particular') { doc.text(`Empresa: ${clientCompany}`, margin, y); y += 5; }
    doc.text(`Email: ${clientEmail}`, margin, y); y += 5;
    doc.text(`Tel: ${clientPhone}`, margin, y); y += 5;
    if (clientCity) { doc.text(`Ciudad: ${clientCity}`, margin, y); }
    const yClientEnd = y;

    // Columna derecha: Evento
    y = startY;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
    doc.text('Evento', midX + 3, y); y += 6;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text(`Fecha: ${eventDate}`, midX + 3, y); y += 5;
    doc.text(`Ciudad: ${eventCity}`, midX + 3, y); y += 5;
    const addrText = eventAddress.length > 35 ? eventAddress.substring(0, 35) + '...' : eventAddress;
    doc.text(`Dir: ${addrText}`, midX + 3, y); y += 5;
    doc.text(`Horario: ${evStart} – ${evEnd}`, midX + 3, y);
    const yEventEnd = y;

    y = Math.max(yClientEnd, yEventEnd) + 10;

    // ── TABLA DE EQUIPOS — igual que cotizaciones ────────────────────
    // Encabezados: # | IMG | Código | Producto | Horas | V. Unit | Subtotal
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(0, 0, 0);
    doc.text('#',        margin,            y);
    doc.text('IMG',      margin + 10,       y);
    doc.text('Código',   margin + 28,       y);
    doc.text('Producto', margin + 54,       y);
    doc.text('Horas',    pageWidth - 90,    y);
    doc.text('V. Unit',  pageWidth - 55, y, { align: 'right' });
    doc.text('Subtotal', pageWidth - 20, y, { align: 'right' });
    y += 4;
    doc.setLineWidth(0.2); doc.setDrawColor(230, 230, 230);
    doc.line(margin, y, pageWidth - margin, y);
    y += 4;

    const rowH = 22;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);

    const items = order.items || [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (y + rowH + 10 > 282) { doc.addPage(); y = 18; }

      // Fila con borde
      doc.setDrawColor(230); doc.setLineWidth(0.3);
      doc.rect(margin, y - 4, pageWidth - margin * 2, rowH);

      const baseY = y + 2;

      // # número
      doc.setFont('helvetica', 'bold');
      doc.text(String(i + 1), margin + 2, baseY);

      // Imagen 16×16
      try {
        const imgData = await loadImgDataURL(it.cover || placeholderImg);
        if (imgData) {
          const imgType = imgData.startsWith('data:image/png') ? 'PNG' : 'JPEG';
          doc.addImage(imgData, imgType, margin + 6, y - 2, 16, 16);
        }
      } catch {}

      // Código
      doc.setFont('helvetica', 'bold');
      doc.text(String(it.code || ''), margin + 28, baseY);

      // Nombre del producto
      doc.setFont('helvetica', 'normal');
      const nameLines = doc.splitTextToSize(it.name || '', 40);
      doc.text(nameLines, margin + 54, baseY);

      // Horas · Cantidad
      const hoursText = [it.hours ? `${it.hours}h` : '', it.qty && it.qty !== 1 ? `x${it.qty}` : ''].filter(Boolean).join(' · ');
      doc.text(hoursText || '—', pageWidth - 90, baseY);

      // Precios
      doc.text(fmtCOP(it.unitPrice), pageWidth - 55, baseY, { align: 'right' });
      doc.text(fmtCOP(it.lineTotal), pageWidth - 20, baseY, { align: 'right' });

      y += rowH;
    }

    y += 6;

    // ── TOTALES (izquierda) + QR de pago (derecha) ───────────────────
    if (y > 230) { doc.addPage(); y = 18; }

    const subtotalAmt  = order.totals?.subtotal   || 0;
    const ivaPercent   = order.totals?.ivaPercent ?? 19;
    // Calcular IVA si no está guardado pero hay subtotal y porcentaje
    const ivaAmt       = order.totals?.iva || (ivaPercent > 0 && subtotalAmt > 0 ? Math.round(subtotalAmt * ivaPercent / 100) : 0);
    const extraHoursN  = order.totals?.extraHours || 0;
    const extraCostAmt = order.totals?.extraCost  || 0;
    // Usar transport de totals, o de transportDetails como fallback
    const transportAmt = order.totals?.transport || order.transportDetails?.cost || 0;
    const totalAmt     = order.totals?.total      || 0;
    const anticipo     = Math.round(totalAmt * 0.5);
    const saldo        = totalAmt - anticipo;

    const qrSize  = 44;
    const qrX     = pageWidth - margin - qrSize;  // QR en la derecha
    const vX      = qrX - 6;                      // valores alineados justo antes del QR
    const totalsY = y;                             // y fijo para alinear QR y totales

    // QR de pago — derecha
    const payQrData = await loadImgDataURL(PAYMENT_QR_URL);
    if (payQrData) {
      try { doc.addImage(payQrData, 'JPEG', qrX, totalsY, qrSize, qrSize); } catch {}
      doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...PURPLE);
      doc.text('Escanear para pagar', qrX + qrSize / 2, totalsY + qrSize + 5, { align: 'center' });
    }

    // Totales — izquierda (sin línea de descuento)
    const fmtVal = (v) => '$ ' + Number(v || 0).toLocaleString('es-CO');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(0, 0, 0);
    doc.text('Subtotal',              margin, y); doc.setFont('helvetica', 'normal'); doc.text(fmtVal(subtotalAmt),  vX, y, { align: 'right' }); y += 6;
    doc.setFont('helvetica', 'bold');
    doc.text(`IVA (${ivaPercent}%)`,  margin, y); doc.setFont('helvetica', 'normal'); doc.text(fmtVal(ivaAmt),       vX, y, { align: 'right' }); y += 6;
    if (extraCostAmt > 0) {
      doc.setFont('helvetica', 'bold');
      doc.text(`Horas Extra (${extraHoursN}h)`, margin, y); doc.setFont('helvetica', 'normal'); doc.text(fmtVal(extraCostAmt), vX, y, { align: 'right' }); y += 6;
    }
    doc.setFont('helvetica', 'bold');
    doc.text('Transporte',            margin, y); doc.setFont('helvetica', 'normal'); doc.text(fmtVal(transportAmt), vX, y, { align: 'right' }); y += 8;

    // Línea separadora
    doc.setLineWidth(0.3); doc.setDrawColor(180, 180, 200);
    doc.line(margin, y, vX, y); y += 6;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(0, 0, 0);
    doc.text('TOTAL', margin, y); doc.text(fmtVal(totalAmt), vX, y, { align: 'right' }); y += 8;

    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...GRAY);
    doc.text(`Anticipo 50% (para separar): ${fmtVal(anticipo)}`, margin, y); y += 5;
    doc.text(`Saldo contra entrega:        ${fmtVal(saldo)}`,    margin, y); y += 5;

    // Avanzar más allá de ambos elementos (totales + QR)
    y = Math.max(y, totalsY + qrSize + 10);

    // Términos de pago
    if (y > 272) { doc.addPage(); y = 18; }
    doc.setFontSize(9); doc.setFont('helvetica', 'italic'); doc.setTextColor(...GRAY);
    const terms = doc.splitTextToSize('Para separar: 50% de anticipo. 50% restante contra entrega (montaje terminado y funcionando).', pageWidth - margin * 2);
    doc.text(terms, margin, y);
    y += terms.length * 5 + 6;

    // ── NOTAS IMPORTANTES (en rojo) ──────────────────────────────────
    const RED = [200, 30, 30];
    const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent((eventCity + ' ' + eventAddress).trim())}`;
    const deadline48h = (() => {
      const d = new Date(order.at || order.createdDate || Date.now());
      d.setHours(d.getHours() + 48);
      return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    })();
    const NOTES = [
      'La falta de fluido eléctrico o que las instalaciones del evento no soporten el fluido eléctrico requerido por nuestros equipos es responsabilidad del contratante.',
      'Si los ingresos o ascensores son angostos, el evento queda en un nivel diferente al primer piso, o el trayecto desde donde se descarga el camión al evento es una distancia larga, debe informarnos ANTES del evento.',
      `Esta orden de servicio genera una separación de equipos por 48 horas (hasta el ${deadline48h}), tiempo del cual dispone para hacer el anticipo. Pasado ese tiempo los equipos se liberarán y deberá reiniciar el proceso para verificar disponibilidad.`,
      `Para confirmar la reserva: envíe el comprobante de pago al WhatsApp +57 3334008723 o al correo cotizaciones@inflablesdasilva.com indicando el No. de orden ${order.id}. El comprobante facilita el registro contable y la confirmación inmediata.`,
      'Esta cotización u orden de servicio fue elaborada por un sistema automatizado. Pueden existir ajustes menores a favor o en contra. Usted decide si continuar con la contratación.',
      `Confirme la dirección del evento: ${mapsUrl}`,
    ];

    if (y > 260) { doc.addPage(); y = 18; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...RED);
    doc.text('NOTAS IMPORTANTES', margin, y); y += 6;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
    for (let ni = 0; ni < NOTES.length; ni++) {
      const noteLines = doc.splitTextToSize(`${ni + 1}. ${NOTES[ni]}`, pageWidth - margin * 2 - 2);
      if (y + noteLines.length * 4.5 > 272) { doc.addPage(); y = 18; }
      doc.setTextColor(...RED);
      doc.text(noteLines, margin, y);
      y += noteLines.length * 4.5 + 2;
    }
    y += 4;

    // Condiciones
    if (order.conditions?.length > 0) {
      if (y > 265) { doc.addPage(); y = 18; }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(0, 0, 0);
      doc.text('Condiciones', margin, y); y += 5;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(...GRAY);
      for (const cond of order.conditions) {
        const lines = doc.splitTextToSize(`• ${cond}`, pageWidth - margin * 2);
        if (y + lines.length * 4.5 > 272) { doc.addPage(); y = 18; }
        doc.text(lines, margin, y);
        y += lines.length * 4.5 + 2;
      }
    }

    // ── PIE DE PÁGINA — igual que pdfQuote.js (texto + iconos de redes) ──
    // Precargar iconos de redes sociales
    const iconUrls = [
      'https://cdn-icons-png.flaticon.com/512/733/733585.png',   // WhatsApp
      'https://cdn-icons-png.flaticon.com/512/733/733547.png',   // Facebook
      'https://cdn-icons-png.flaticon.com/512/2111/2111463.png', // Instagram
      'https://cdn-icons-png.flaticon.com/512/3046/3046125.png', // TikTok
      'https://cdn-icons-png.flaticon.com/512/5968/5968830.png', // Twitter/X
      'https://cdn-icons-png.flaticon.com/512/1384/1384060.png', // YouTube
    ];
    const loadedIcons = [];
    for (const url of iconUrls) {
      const d = await loadImgDataURL(url);
      if (d) loadedIcons.push(d);
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let pg = 1; pg <= pageCount; pg++) {
      doc.setPage(pg);
      const pageH = doc.internal.pageSize.getHeight();
      // Línea separadora
      doc.setLineWidth(0.2); doc.setDrawColor(210, 210, 210);
      doc.line(margin + 5, pageH - 17, pageWidth - margin - 5, pageH - 17);
      // Texto webs + email
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(0, 0, 0);
      doc.text(
        'www.inflablesdasilva.com  -  www.dasilvafunpark.com  •  contactenos@inflablesdasilva.com',
        pageWidth / 2, pageH - 12, { align: 'center' }
      );
      // Iconos de redes sociales centrados
      if (loadedIcons.length > 0) {
        const iconSize = 4;
        const gap = 3;
        const totalW = loadedIcons.length * iconSize + (loadedIcons.length - 1) * gap;
        let ix = (pageWidth - totalW) / 2;
        for (const ic of loadedIcons) {
          try { doc.addImage(ic, 'PNG', ix, pageH - 8, iconSize, iconSize); } catch {}
          ix += iconSize + gap;
        }
      }
    }

    doc.save(`${order.id}.pdf`);
  };
  
  const exportWarehouseOrderPDF = async (order) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const M = 14;
    const GREEN = [22, 163, 74];
    const LIGHT_GREEN = [240, 253, 244];
    const GRAY = [100, 116, 139];
    let y = 12;

    // ── Header ────────────────────────────────────────────────────────
    try { const ld = await urlToDataURL(BRAND_LOGO_URL); if (ld) doc.addImage(ld, 'PNG', M, y, 26, 22); } catch {}
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18);
    doc.setTextColor(...GREEN);
    doc.text('ORDEN DE BODEGA', M + 30, y + 9);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(0, 0, 0);
    doc.text('Inflables Da Silva & FunPark', M + 30, y + 15);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
    doc.text(order.id, pageWidth - M, y + 9, { align: 'right' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
    const dateStr = new Date().toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.text(dateStr, pageWidth - M, y + 15, { align: 'right' });
    y += 26;
    doc.setDrawColor(...GREEN); doc.setLineWidth(0.8);
    doc.line(M, y, pageWidth - M, y);
    y += 5;

    // ── Info box ──────────────────────────────────────────────────────
    doc.setFillColor(...LIGHT_GREEN);
    doc.roundedRect(M, y, pageWidth - M * 2, 20, 2, 2, 'F');
    doc.setFontSize(8.5); doc.setFont('helvetica', 'bold');
    doc.text('ORIGEN:', M + 3, y + 6); doc.text('DESTINO:', M + 3, y + 13);
    doc.setFont('helvetica', 'normal');
    doc.text(`${order.originBranch?.name || ''} — ${order.originBranch?.city || ''}`, M + 24, y + 6);
    doc.text(`${order.destination?.city || ''} — ${order.destination?.address || ''}`, M + 24, y + 13);
    const halfX = pageWidth / 2 + 4;
    doc.setFont('helvetica', 'bold');
    doc.text('HORARIO:', halfX, y + 6); doc.text('OS REF:', halfX, y + 13);
    doc.setFont('helvetica', 'normal');
    doc.text(`${order.schedule?.start || '—'} a ${order.schedule?.end || '—'}`, halfX + 22, y + 6);
    doc.text(order.serviceOrderId || '—', halfX + 22, y + 13);
    y += 26;

    // ── Per-equipment sections ────────────────────────────────────────
    const STD_ELEMENTS = [
      ['Motor (color/marca)', '1', '', ''],
      ['Extensión eléctrica', '1', '', ''],
      ['Caja eléctrica', '1', '', ''],
      ['Uniformes', '', '', ''],
      ['Kit de reparación', '1', '', ''],
      ['Estacas', '', '', ''],
      ['Tubos de PVC', '', '', ''],
      ['Banderín', '1', '', ''],
    ];

    for (const item of (order.items || [])) {
      if (y > 220) { doc.addPage(); y = 14; }

      // Green header bar for equipment
      doc.setFillColor(...GREEN);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
      doc.roundedRect(M, y, pageWidth - M * 2, 9, 1, 1, 'F');
      doc.text(`${item.name || '—'}  ×${item.qty || 1}  ${item.code ? `(${item.code})` : ''}`, M + 3, y + 6.5);
      doc.setTextColor(0, 0, 0);
      y += 11;

      const photoW = 44;
      const photoH = 44;
      const tableX = M + photoW + 4;

      // Photo with green border
      doc.setDrawColor(...GREEN); doc.setLineWidth(0.6);
      doc.rect(M, y, photoW, photoH);
      try {
        const imgData = await urlToDataURL(item.cover || placeholderImg);
        if (imgData) doc.addImage(imgData, 'JPEG', M + 0.5, y + 0.5, photoW - 1, photoH - 1);
      } catch {
        doc.setFillColor(230, 235, 230);
        doc.rect(M + 0.5, y + 0.5, photoW - 1, photoH - 1, 'F');
        doc.setFontSize(8); doc.setTextColor(...GRAY);
        doc.text('Sin foto', M + photoW / 2, y + photoH / 2, { align: 'center' });
        doc.setTextColor(0, 0, 0);
      }

      // Elements table next to photo
      const elemRows = [
        ['Inflable', String(item.qty || 1), '', ''],
        ...STD_ELEMENTS,
      ];

      autoTable(doc, {
        startY: y,
        margin: { left: tableX, right: M },
        tableWidth: pageWidth - tableX - M,
        head: [['ELEMENTOS', 'CANT.', 'SALIDA', 'ENTRADA']],
        body: elemRows,
        styles: { fontSize: 7.5, cellPadding: 1.4, lineColor: [200, 210, 200], lineWidth: 0.2 },
        headStyles: { fillColor: GREEN, textColor: 255, fontStyle: 'bold', fontSize: 7.5, halign: 'center' },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 12, halign: 'center' },
          2: { cellWidth: 16, halign: 'center' },
          3: { cellWidth: 16, halign: 'center' },
        },
        didParseCell: (data) => {
          if (data.row.index === 0 && data.section === 'body') {
            data.cell.styles.fillColor = LIGHT_GREEN;
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });

      const tableEnd = doc.lastAutoTable.finalY;
      y = Math.max(y + photoH, tableEnd) + 6;
    }

    // ── ADICIONALES section ───────────────────────────────────────────
    if (y > 240) { doc.addPage(); y = 14; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
    doc.setTextColor(...GREEN);
    doc.text('ADICIONALES', M, y);
    doc.setTextColor(0, 0, 0);
    y += 3;

    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M },
      head: [['ELEMENTO', 'CANTIDAD', 'SALIDA', 'ENTRADA']],
      body: [
        ['Kit de reparación general', '', '', ''],
        ['Uniformes (adicionales)', '', '', ''],
        ['Extensiones eléctricas (adicionales)', '', '', ''],
        ['Cajas eléctricas (adicionales)', '', '', ''],
        ['Motores (repuesto)', '', '', ''],
      ],
      styles: { fontSize: 8.5, cellPadding: 2, lineColor: [200, 210, 200], lineWidth: 0.2 },
      headStyles: { fillColor: GREEN, textColor: 255, fontStyle: 'bold', halign: 'center' },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 22, halign: 'center' },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 22, halign: 'center' },
      },
    });

    y = doc.lastAutoTable.finalY + 8;

    // Notes
    if (order.notes) {
      doc.setFont('helvetica', 'italic'); doc.setFontSize(8.5); doc.setTextColor(...GRAY);
      doc.text(`Notas: ${order.notes}`, M, y);
      y += 8;
    }

    // Signature lines
    if (y > 270) { doc.addPage(); y = 14; }
    y += 4;
    doc.setDrawColor(...GRAY); doc.setLineWidth(0.4);
    doc.line(M, y + 10, M + 55, y + 10);
    doc.line(pageWidth - M - 55, y + 10, pageWidth - M, y + 10);
    doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...GRAY);
    doc.text('Firma despacho', M + 27.5, y + 15, { align: 'center' });
    doc.text('Firma recibo', pageWidth - M - 27.5, y + 15, { align: 'center' });

    doc.save(`${order.id}.pdf`);
  };

  /** ===== NOTIFICACIONES DINÁMICAS ===== */
  const notifications = useMemo(() => {
    const notifs = [];
    const now = new Date();
    const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
    // Eventos de hoy
    const todayEvents = calendarEvents.filter(ev => {
      const d = new Date(ev.start);
      return d.toDateString() === now.toDateString() && ev.type === 'evento';
    });
    todayEvents.forEach(ev => notifs.push({ id: 'ev-' + ev.id, icon: '📅', text: `Evento hoy: ${ev.title}`, sub: ev.location || '', color: '#3b82f6', action: () => setActiveSection('calendario') }));
    // Eventos mañana
    const tomorrowEvents = calendarEvents.filter(ev => {
      const d = new Date(ev.start);
      return d.toDateString() === tomorrow.toDateString() && ev.type === 'evento';
    });
    tomorrowEvents.forEach(ev => notifs.push({ id: 'evt-' + ev.id, icon: '📆', text: `Mañana: ${ev.title}`, sub: ev.location || '', color: '#f59e0b', action: () => setActiveSection('calendario') }));
    // Órdenes pendientes recientes
    const pendingOrders = serviceOrders.filter(o => o.status === 'pending' || o.status === 'pendiente');
    if (pendingOrders.length > 0) {
      notifs.push({ id: 'ord-pend', icon: '📋', text: `${pendingOrders.length} orden(es) pendiente(s)`, sub: 'Requieren atención', color: '#f59e0b', action: () => setActiveSection('ordenes') });
    }
    return notifs;
  }, [calendarEvents, serviceOrders]);

  /** ===== NUEVO PANEL DE CONTROL (BIEN PRO) ===== */
  const renderPanel = () => {
    // Vista personalizada para empleados no-admin
    if (!isAdminUser) {
      return <EmployeeDashboard calendarEvents={calendarEvents} setActiveSection={setActiveSection} />;
    }

    // 1. Calculamos cuánto dinero suman todas las órdenes
    const totalRevenue = serviceOrders.reduce((acc, order) => acc + (order.totals?.total || 0), 0);
    
    // 2. Buscamos eventos para los próximos 7 días
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    
    const upcomingEvents = calendarEvents.filter(ev => {
      const evDate = new Date(ev.start);
      return evDate >= today && evDate <= nextWeek && ev.type === 'evento';
    }).sort((a,b) => new Date(a.start) - new Date(b.start));

    // 3. Últimas 5 órdenes
    const recentOrders = [...serviceOrders].sort((a,b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

    // 4. Productos destacados (ejemplo: los 3 primeros)
    const topProducts = products.slice(0, 3); 

    // Diseño de la Tarjeta de Números
    const StatCard = ({ title, value, subtext, icon, color, gradient }) => (
      <Paper sx={{ 
        p: 3, borderRadius: 3, height: '100%',
        background: gradient || '#fff', color: gradient ? '#fff' : 'inherit',
        position: 'relative', overflow: 'hidden'
      }} elevation={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ opacity: 0.8, fontWeight: 600, textTransform: 'uppercase' }}>{title}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, my: 1 }}>{value}</Typography>
          </Box>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: gradient ? 'rgba(255,255,255,0.2)' : `${color}15`, color: gradient ? '#fff' : color }}>
            {icon}
          </Box>
        </Box>
        <Typography variant="body2" sx={{ opacity: 0.8, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TrendingUpIcon sx={{ fontSize: 16 }} /> {subtext}
        </Typography>
      </Paper>
    );

    return (
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary' }}>Panel General</Typography>
          <Typography variant="body1" color="text.secondary">Resumen operativo de hoy.</Typography>
        </Box>

        {/* INDICADORES (TARJETAS DE COLORES) */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Ingresos Totales" value={`$${totalRevenue.toLocaleString('es-CO', { maximumFractionDigits: 0 })}`} subtext="Acumulado histórico" icon={<MoneyIcon />} color="#10b981" gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Órdenes Activas" value={serviceOrders.length} subtext="Total registradas" icon={<ServiceOrderIcon />} color="#6366f1" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Clientes" value={clients.length} subtext="Base de datos" icon={<ClientsIcon />} color="#f59e0b" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Eventos Próx." value={upcomingEvents.length} subtext="En 7 días" icon={<CalendarIcon />} color="#ef4444" />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          {/* COLUMNA IZQUIERDA */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 0, borderRadius: 3, overflow: 'hidden' }} elevation={2}>
              <Box sx={{ p: 3, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Órdenes Recientes</Typography>
                <Button size="small" endIcon={<TimelineIcon />} onClick={() => setActiveSection('service-orders')}>Ver todas</Button>
              </Box>
              <Box>
                {recentOrders.map((order) => (
                  <Box key={order.id} sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid #f8fafc' }}>
                    <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: '#eff6ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                      <ServiceOrderIcon />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{order.client?.name || 'Cliente'}</Typography>
                      <Typography variant="body2" color="text.secondary">{order.event?.city} • {order.items?.length ?? 0} ítems</Typography>
                    </Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#10b981' }}>${(order.totals?.total || 0).toLocaleString('es-CO')}</Typography>
                  </Box>
                ))}
                {recentOrders.length === 0 && <Box sx={{ p: 4, textAlign: 'center' }}>No hay órdenes recientes.</Box>}
              </Box>
            </Paper>

            {/* ACCESOS RÁPIDOS */}
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>Accesos Rápidos</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Button fullWidth variant="contained" size="large" startIcon={<PostAddIcon />} sx={{ py: 2, bgcolor: '#1e293b' }} onClick={() => { resetClientForm(); setActiveSection('cotizaciones'); }}>Nueva Cotización</Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button fullWidth variant="contained" size="large" startIcon={<GroupAddIcon />} sx={{ py: 2, bgcolor: '#1e293b' }} onClick={() => { resetClientForm(); setActiveSection('clients'); }}>Registrar Cliente</Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button fullWidth variant="contained" size="large" startIcon={<CalendarIcon />} sx={{ py: 2, bgcolor: '#1e293b' }} onClick={() => setActiveSection('calendar')}>Ver Agenda</Button>
              </Grid>
            </Grid>
          </Grid>

          {/* COLUMNA DERECHA */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 0, borderRadius: 3, mb: 3 }} elevation={2}>
              <Box sx={{ p: 2, borderBottom: '1px solid #f1f5f9', bgcolor: '#f8fafc' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon color="primary" fontSize="small" /> Agenda Semanal
                </Typography>
              </Box>
              <Box>
                {upcomingEvents.length > 0 ? upcomingEvents.map((ev) => (
                   <Box key={ev.id} sx={{ p: 2, borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 1.5 }}>
                     <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff1f2', color: '#e11d48', borderRadius: 2, width: 50, height: 50 }}>
                       <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>{new Date(ev.start).getDate()}</Typography>
                     </Box>
                     <Box>
                       <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{ev.title}</Typography>
                       <Typography variant="caption" color="text.secondary">{ev.location}</Typography>
                     </Box>
                   </Box>
                )) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <CheckCircleIcon sx={{ color: '#cbd5e1', fontSize: 40, mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">¡Todo libre!</Typography>
                  </Box>
                )}
              </Box>
            </Paper>

          </Grid>
        </Grid>
      </Container>
    );
  };

  /** ===== Productos (Alquiler + Ventas unificados) ===== */
  const renderProducts = () => (
    <Container maxWidth="xl">
      {/* ── Pestañas de navegación ── */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={productsTab}
          onChange={(_, v) => setProductsTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: '1px solid #e0e0e0' }}
        >
          <Tab label="🏠 Alquiler" />
          <Tab label="🕹️ Máquinas Arcade" />
          <Tab label="🎪 Inflables Comerciales" />
          <Tab label="🏗️ Parques Playground" />
        </Tabs>
      </Paper>

      {/* ── Tab Alquiler ── */}
      {productsTab === 0 && <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          Alquiler ({filteredProducts.length})
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ borderRadius: 2 }} onClick={handleAddProduct}>
          Agregar Producto
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth placeholder="Buscar productos…"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select value={selectedCategory} label="Categoría" onChange={(e) => setSelectedCategory(e.target.value)}>
                {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={() => { setSearchTerm(''); setSelectedCategory('Todas'); setPage(1); }}>
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {current.map((p) => {
          const imgs = (p.images && p.images.length > 0) ? p.images : [coverOf(p)];
          const idx = cardImageIdx[p.uid] || 0;
          const safeIdx = Math.min(idx, imgs.length - 1);
          return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={p.uid}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ position: 'relative', height: 200, overflow: 'hidden', bgcolor: '#f1f5f9' }} onContextMenu={noDownload}>
                <CardMedia component="img" height="200" image={imgs[safeIdx]} alt={p.nombre} sx={{ objectFit: 'cover', ...imgProtectSx }} onDragStart={noDownload} />
                {imgs.length > 1 && (<>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCardImageIdx(prev => ({ ...prev, [p.uid]: (safeIdx - 1 + imgs.length) % imgs.length })); }}
                    sx={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' }, width: 28, height: 28 }}>
                    <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCardImageIdx(prev => ({ ...prev, [p.uid]: (safeIdx + 1) % imgs.length })); }}
                    sx={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' }, width: 28, height: 28 }}>
                    <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                  <Box sx={{ position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 0.5 }}>
                    {imgs.map((_, i) => (
                      <Box key={i} sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: i === safeIdx ? '#fff' : 'rgba(255,255,255,0.5)', border: '1px solid rgba(0,0,0,0.2)', cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); setCardImageIdx(prev => ({ ...prev, [p.uid]: i })); }} />
                    ))}
                  </Box>
                </>)}
              </Box>
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h6" noWrap>{p.nombre}</Typography>
                <Chip label={p.categoria} size="small" sx={{ mb: 1 }} color="primary" variant="outlined" />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Código: {p.codigo}</Typography>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                  ${p.precio_alquiler_5horas ? Number(p.precio_alquiler_5horas).toLocaleString() : 'N/A'}
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => handleViewProduct(p)}>Ver</Button>
                <IconButton size="small" color="primary" onClick={() => handleEditProduct(p)}><EditIcon /></IconButton>
              </CardActions>
            </Card>
          </Grid>
          );
        })}
      </Grid>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" size="large" />
        </Box>
      )}
      </>}

      {/* ── Tabs Ventas (Arcade / Inflables / Playground) ── */}
      {productsTab > 0 && <VentasModule forceTab={productsTab - 1} />}

    </Container>
  );

  /** ===== Fichas Técnicas ===== */
  /** ===== Renderizar Calendario PROFESIONAL ===== */
  const renderCalendar = () => {
    const today = new Date();
    const currentMonth = selectedDate ? new Date(selectedDate) : today;
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Generar días del mes
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    
    // 1. FILTRADO INTERACTIVO
    let monthEvents = calendarEvents.filter(event => {
      const eventDate = new Date(event.start);
      // Filtro por Mes
      const sameMonth = eventDate.getMonth() === month && eventDate.getFullYear() === year;
      if (!sameMonth) return false;
      // Filtro por Tipo (Botones)
      if (calendarFilter !== 'all' && event.type !== calendarFilter) return false;
      return true;
    });

    // 2. DETECCIÓN DE CONFLICTOS (Lógica Visual)
    // Buscamos si hay equipos usados al mismo tiempo en diferentes eventos
    const eventsWithConflict = new Set();
    monthEvents.forEach(ev1 => {
        if (ev1.type !== 'evento' || !ev1.equipment || ev1.equipment.length === 0) return;
        
        // Comparamos contra todos los demás eventos
        const hasConflict = monthEvents.some(ev2 => {
            if (ev1.id === ev2.id || ev2.type !== 'evento') return false; // No compararse a sí mismo ni con logística
            
            // Chequeo de hora
            const start1 = new Date(ev1.start); const end1 = new Date(ev1.end);
            const start2 = new Date(ev2.start); const end2 = new Date(ev2.end);
            const timeOverlap = (start1 < end2 && end1 > start2);
            
            if (!timeOverlap) return false;

            // Chequeo de equipos (Intersección)
            const sharedEquipment = ev1.equipment.filter(eq => ev2.equipment?.includes(eq));
            return sharedEquipment.length > 0;
        });

        if (hasConflict) eventsWithConflict.add(ev1.id);
    });
    
    // Organizar eventos por día
    const eventsByDay = {};
    monthEvents.forEach(event => {
      const day = new Date(event.start).getDate();
      if (!eventsByDay[day]) eventsByDay[day] = [];
      eventsByDay[day].push(event);
    });
    
    const days = [];
    
    // Días vacíos previos
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(<Grid item xs={12/7} key={`empty-${i}`}><Box sx={{ height: 120, border: '1px solid #f1f5f9', bgcolor: '#f8fafc' }}></Box></Grid>);
    }
    
    // Celdas de días
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;
      const dayEvents = eventsByDay[day] || [];
      
      days.push(
        <Grid item xs={12/7} key={day}>
          <Paper sx={{ 
            height: 120, 
            p: 0.5, 
            border: isToday ? '2px solid #6366f1' : '1px solid #e2e8f0',
            backgroundColor: isToday ? '#fff' : '#fff',
            position: 'relative',
            transition: '0.2s',
            '&:hover': { backgroundColor: '#f8fafc', borderColor: '#cbd5e1' }
          }}
          onClick={() => handleDayClick(year, month, day)}>
            
            {/* Número del día */}
            <Box sx={{ display:'flex', justifyContent:'space-between', px: 0.5 }}>
                 <Typography variant="body2" sx={{ fontWeight: isToday ? 700 : 500, color: isToday ? '#6366f1' : '#64748b', bgcolor: isToday ? '#e0e7ff' : 'transparent', px: 0.8, borderRadius: 1 }}>
                  {day}
                </Typography>
                {dayEvents.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.65rem'}}>
                        {dayEvents.length} items
                    </Typography>
                )}
            </Box>

            {/* Lista de Eventos */}
            <Box sx={{ mt: 0.5, maxHeight: 88, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {dayEvents.slice(0, 4).map((event, idx) => {
                const isConflict = eventsWithConflict.has(event.id);
                // Colores dinámicos
                let bg = '#f3f4f6'; let color = '#374151'; let border = 'transparent';
                
                if (isConflict) { 
                    bg = '#fef2f2'; color = '#dc2626'; border = '1px solid #fca5a5'; 
                } else if (event.type === 'evento') { 
                    bg = '#ecfdf5'; color = '#047857'; border = '1px solid #a7f3d0';
                } else if (event.type === 'montaje') { 
                    bg = '#fffbeb'; color = '#b45309'; border = '1px solid #fde68a';
                } else if (event.type === 'transporte') { 
                    bg = '#eff6ff'; color = '#1d4ed8'; border = '1px solid #bfdbfe';
                }

                return (
                <Box key={idx} sx={{
                  p: '2px 4px',
                  borderRadius: 1,
                  fontSize: '0.70rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: bg,
                  color: color,
                  border: border,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  '&:hover': { filter: 'brightness(0.95)' }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedEvent(event);
                  setOpenEventDialog(true);
                }}>
                  {isConflict && <Typography component="span" sx={{fontSize:'10px'}}>⚠️</Typography>}
                  {event.title}
                </Box>
              )})}
              {dayEvents.length > 4 && (
                <Typography variant="caption" align="center"
                  onClick={(e) => { e.stopPropagation(); handleDayClick(year, month, day); }}
                  sx={{ display:'block', fontSize: '0.65rem', cursor: 'pointer', color: '#6366f1', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}>
                  +{dayEvents.length - 4} más
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      );
    }
    
    return (
      <Container maxWidth="xl">
        {/* --- ENCABEZADO SUPERIOR --- */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Calendario Operativo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gestión de eventos y logística
              </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" color="secondary" onClick={() => setOpenPdfDialog(true)} startIcon={<PdfIcon />}>
              Reporte PDF
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewEventDialog(true)}>
              Nuevo
            </Button>
            <Button variant="outlined" onClick={() => setSelectedDate(new Date().toISOString().slice(0, 10))}>
              Hoy
            </Button>
            <Button variant="outlined" color="info" onClick={async () => {
              const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';
              localStorage.setItem('calendarEvents_v1', JSON.stringify([]));
              const calRes = await fetch(`${apiBase}/calendar`);
              if (calRes.ok) {
                const calData = await calRes.json();
                if (calData.ok && Array.isArray(calData.events)) {
                  setCalendarEvents(calData.events.map(ev => ({
                    id: ev.eventId || ev._id, title: ev.title, type: ev.type || 'evento',
                    client: ev.client?.name || '', clientPhone: ev.client?.phone || '',
                    clientEmail: ev.client?.email || '', coordinator: ev.coordinator || null,
                    start: ev.start ? ev.start.replace('Z','').replace(/\.\d+$/,'') : ev.start,
                    end:   ev.end   ? ev.end.replace('Z','').replace(/\.\d+$/,'')   : ev.end,
                    location: ev.location?.address ? `${ev.location.city||''} - ${ev.location.address}` : (ev.location?.city||''),
                    equipment: ev.equipment?.map(e => e.code||e.name||e) || [],
                    equipmentNames: ev.equipment?.map(e => e.name||e.code||'') || [],
                    km: ev.km||0,
                    mapsUrl: ev.location?.mapsUrl || (ev.location?.address||ev.location?.city ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${ev.location?.address||''}, ${ev.location?.city||''}, Colombia`)}` : ''),
                    status: ev.status||'programado', description: ev.description||ev.notes||'',
                    quoteNumber: ev.quoteNumber||'', orderNumber: ev.orderNumber||'',
                    financials: ev.financials||null, relatedEvent: ev.parentEventId||null, fromDB: true
                  })));
                  setSnackbar({ open:true, message:'🔄 Calendario actualizado desde servidor', severity:'success' });
                }
              }
            }} startIcon={<RefreshIcon />}>
              Sincronizar
            </Button>
          </Box>
        </Box>

        {/* --- BARRA DE CONTROL Y NAVEGACIÓN (Paper Superior) --- */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }} elevation={0} variant="outlined">
          <Grid container alignItems="center" spacing={2}>
            
            {/* Navegación Mes */}
            <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton onClick={() => navigateMonth(-1)} size="small" sx={{border: '1px solid #e2e8f0'}}><ArrowBackIosNewIcon fontSize="small"/></IconButton>
                  <Typography variant="h5" sx={{ fontWeight: 700, minWidth: 140, textAlign: 'center' }}>
                    {monthNames[month]} {year}
                  </Typography>
                  <IconButton onClick={() => navigateMonth(1)} size="small" sx={{border: '1px solid #e2e8f0'}}><ArrowForwardIosIcon fontSize="small"/></IconButton>
                </Box>
            </Grid>

            {/* Filtros Interactivos (Chips) */}
            <Grid item xs={12} md={8} sx={{ display: 'flex', justifyContent: {xs: 'flex-start', md: 'flex-end'}, gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ mr: 1, alignSelf: 'center', color: 'text.secondary', fontWeight: 600 }}>
                Filtrar vista:
              </Typography>
              
              <Chip 
                label="Todos" 
                clickable 
                color={calendarFilter === 'all' ? 'default' : 'default'} 
                variant={calendarFilter === 'all' ? 'filled' : 'outlined'}
                onClick={() => setCalendarFilter('all')}
                sx={{ fontWeight: 600 }}
              />
              <Chip 
                icon={<Box sx={{ width: 10, height: 10, bgcolor: '#059669', borderRadius: '50%' }} />} 
                label="Eventos" 
                clickable 
                onClick={() => setCalendarFilter('evento')}
                variant={calendarFilter === 'evento' ? 'filled' : 'outlined'}
                color={calendarFilter === 'evento' ? 'success' : 'default'}
                sx={{ borderColor: '#059669', color: calendarFilter === 'evento' ? '#fff' : '#059669', fontWeight: 600 }}
              />
              <Chip 
                icon={<Box sx={{ width: 10, height: 10, bgcolor: '#d97706', borderRadius: '50%' }} />} 
                label="Logística" 
                clickable 
                onClick={() => setCalendarFilter('montaje')}
                variant={calendarFilter === 'montaje' ? 'filled' : 'outlined'}
                color={calendarFilter === 'montaje' ? 'warning' : 'default'}
                sx={{ borderColor: '#d97706', color: calendarFilter === 'montaje' ? '#fff' : '#d97706', fontWeight: 600 }}
              />
              <Chip 
                icon={<Box sx={{ width: 10, height: 10, bgcolor: '#2563eb', borderRadius: '50%' }} />} 
                label="Transporte" 
                clickable 
                onClick={() => setCalendarFilter('transporte')}
                variant={calendarFilter === 'transporte' ? 'filled' : 'outlined'}
                color={calendarFilter === 'transporte' ? 'primary' : 'default'}
                sx={{ borderColor: '#2563eb', color: calendarFilter === 'transporte' ? '#fff' : '#2563eb', fontWeight: 600 }}
              />
            </Grid>
          </Grid>

          {/* Días de la semana Header */}
          <Grid container sx={{ mt: 2, borderTop: '1px solid #f1f5f9', pt: 1 }}>
            {weekDays.map((day) => (
              <Grid item xs={12/7} key={day}>
                <Typography variant="caption" sx={{ display:'block', textAlign: 'center', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
        
        {/* Grid del calendario */}
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
          <Grid container>
            {days}
          </Grid>
        </Box>

        {/* Resumen al pie */}
        <Box sx={{ mt: 2, display: 'flex', gap: 3, color: 'text.secondary', fontSize: '0.875rem' }}>
            <Typography variant="body2">📅 <b>{monthEvents.length}</b> registros visibles</Typography>
            {eventsWithConflict.size > 0 && (
                <Typography variant="body2" color="error" sx={{ fontWeight: 'bold' }}>
                    ⚠️ {eventsWithConflict.size} eventos con conflicto de horario/equipo
                </Typography>
            )}
        </Box>
      </Container>
    );
  };
  
  const navigateMonth = (direction) => {
    const current = new Date(selectedDate);
    current.setMonth(current.getMonth() + direction);
    setSelectedDate(current.toISOString().slice(0, 10));
  };
  
  const handleDayClick = (year, month, day) => {
    const clickedDate = new Date(year, month, day).toISOString().slice(0, 10);
    setSelectedDate(clickedDate);
    setDayDialogDate({ year, month, day });
    setOpenDayDialog(true);
  };

  /** ===== Órdenes de Servicio ===== */
  const renderServiceOrders = () => {
    const today = new Date(); today.setHours(0,0,0,0);

    const getDate = o => {
      const s = o.date || o.eventDetails?.eventDate || o.at;
      if (!s) return new Date(0);
      const parts = String(s).slice(0, 10).split('-').map(Number);
      // Usar hora local (no UTC) para evitar desfase de zona horaria
      return parts.length === 3 ? new Date(parts[0], parts[1] - 1, parts[2]) : new Date(s);
    };
    const getClient = o => (o.client?.name || o.clientData?.name || '').toLowerCase();
    const getCity = o => (o.event?.city || o.eventDetails?.city || '').toLowerCase();

    // Separar próximas (hoy en adelante) e historial (anteriores a hoy)
    const proximas = serviceOrders.filter(o => {
      const d = getDate(o);
      return isNaN(d.getTime()) || d >= today;
    }).sort((a,b) => getDate(a) - getDate(b)); // Más próxima primero por defecto

    const historial = serviceOrders.filter(o => {
      const d = getDate(o);
      return !isNaN(d.getTime()) && d < today;
    }).sort((a,b) => getDate(b) - getDate(a)); // Más reciente primero

    // Ordenamiento configurable
    const sortFn = (a, b) => {
      if (serviceOrdersSort === 'fecha_asc')   return getDate(a) - getDate(b);
      if (serviceOrdersSort === 'fecha_desc')  return getDate(b) - getDate(a);
      if (serviceOrdersSort === 'cliente_asc') return getClient(a).localeCompare(getClient(b), 'es');
      if (serviceOrdersSort === 'ciudad_asc')  return getCity(a).localeCompare(getCity(b), 'es');
      return getDate(a) - getDate(b); // default
    };

    const listaActiva = (serviceOrdersTab === 0 ? [...proximas] : [...historial]).sort(sortFn);

    const renderCard = (o) => {
      const clientName = o.client?.name || o.clientData?.name || '—';
      const city       = o.event?.city || o.eventDetails?.city || '—';
      const address    = o.event?.address || o.eventDetails?.address || '';
      const start      = o.event?.start || o.eventDetails?.startTime || '';
      const end        = o.event?.end || o.eventDetails?.endTime || '';
      const date       = o.date || o.eventDetails?.eventDate || o.at?.slice(0,10) || '';
      const items = (o.items || []).map(it => ({
        name: it.name || it.productName || '',
        qty: it.qty || it.quantity || 1,
        cover: it.cover || '',
        code: it.code || it.productCode || '',
      }));
      const esPasada = serviceOrdersTab === 1;
      return (
        <Grid item xs={12} key={o.id}>
          <Paper sx={{ p:2, borderRadius:2, opacity: esPasada ? 0.85 : 1, borderLeft: esPasada ? '4px solid #94a3b8' : '4px solid #6366f1' }}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={8}>
                <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight:700 }}>{o.id} · Cotización {o.quoteNumber}</Typography>
                  {esPasada && <Chip label="Realizado" size="small" sx={{ bgcolor:'#f1f5f9', color:'#64748b', fontSize:11, height:22 }} />}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {date} · {clientName} · {city}{address ? ` — ${address}` : ''}{start ? ` · ${start}-${end}` : ''}
                </Typography>
                <Divider sx={{ my:1 }} />
                <Grid container spacing={1}>
                  {items.map((it,i)=>(
                    <Grid item key={i}>
                      <Box sx={{ border:'1px solid #e2e8f0', borderRadius:1, p:.5, width:90 }}>
                        <img src={it.cover || placeholderImg} alt={it.name} style={{ width:'100%', height:60, objectFit:'cover', borderRadius:4 }} />
                        <Typography variant="caption" noWrap title={it.name}>{it.name}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">x{it.qty}</Typography>
                      </Box>
                    </Grid>
                  ))}
                  {items.length === 0 && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Sin ítems registrados</Typography></Grid>}
                </Grid>
              </Grid>
              <Grid item xs={12} md={4} sx={{ display:'flex', flexDirection:'column', gap:1 }}>
                {authUser?.role !== 'employee' && !esPasada && (
                  <Button variant="outlined" startIcon={<EditIcon />} onClick={() => handleEditOrder(o, 'service')}>Editar</Button>
                )}
                <Button variant="outlined" startIcon={<PdfIcon />} onClick={() => exportServiceOrderPDF(o)}>Exportar PDF</Button>
                {o.quoteNumber && authUser?.role !== 'employee' && !esPasada && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={sendingOS === o.quoteNumber ? <CircularProgress size={16} color="inherit" /> : <span>📲</span>}
                    disabled={sendingOS === o.quoteNumber}
                    onClick={() => handleResendOSWhatsApp(o)}
                  >
                    Enviar OS por WhatsApp
                  </Button>
                )}
                {authUser?.role !== 'employee' && (
                  <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={async () => {
                    if (!window.confirm(`¿Eliminar orden ${o.id}?`)) return;
                    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api';
                    await fetch(`${apiBase}/orders/service/${o.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                    setServiceOrders(prev => prev.filter(x => x.id !== o.id));
                    // Limpiar caché y refrescar calendario desde DB
                    localStorage.setItem('calendarEvents_v1', JSON.stringify([]));
                    try {
                      const calRes = await fetch(`${apiBase}/calendar`);
                      if (calRes.ok) {
                        const calData = await calRes.json();
                        if (calData.ok && Array.isArray(calData.events)) {
                          setCalendarEvents(calData.events.map(ev => ({
                            id: ev.eventId || ev._id, title: ev.title, type: ev.type || 'evento',
                            client: ev.client?.name || '', clientPhone: ev.client?.phone || '',
                            clientEmail: ev.client?.email || '', coordinator: ev.coordinator || null,
                            start: ev.start ? ev.start.replace('Z','').replace(/\.\d+$/,'') : ev.start,
                            end:   ev.end   ? ev.end.replace('Z','').replace(/\.\d+$/,'')   : ev.end,
                            location: ev.location?.address ? `${ev.location.city||''} - ${ev.location.address}` : (ev.location?.city||''),
                            equipment: ev.equipment?.map(e => e.code||e.name||e) || [],
                            equipmentNames: ev.equipment?.map(e => e.name||e.code||'') || [],
                            km: ev.km||0,
                            mapsUrl: ev.location?.mapsUrl || (ev.location?.address||ev.location?.city ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${ev.location?.address||''}, ${ev.location?.city||''}, Colombia`)}` : ''),
                            status: ev.status||'programado', description: ev.description||ev.notes||'',
                            quoteNumber: ev.quoteNumber||'', orderNumber: ev.orderNumber||'',
                            financials: ev.financials||null, relatedEvent: ev.parentEventId||null, fromDB: true
                          })));
                        }
                      }
                    } catch(e) { /* silencioso */ }
                    setSnackbar({ open:true, message:'Orden eliminada', severity:'info' });
                  }}>Eliminar</Button>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      );
    };

    return (
      <Container maxWidth="xl">
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Órdenes de Servicio</Typography>
        </Box>

        {/* Tabs Próximas / Historial + Ordenamiento */}
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid #e2e8f0', mb:2, flexWrap:'wrap', gap:1 }}>
          <Tabs value={serviceOrdersTab} onChange={(_, v) => setServiceOrdersTab(v)}>
            <Tab label={`📋 Próximas (${proximas.length})`} />
            <Tab label={`📁 Historial (${historial.length})`} />
          </Tabs>
          <Box sx={{ display:'flex', alignItems:'center', gap:1, pb:1 }}>
            <Typography variant="caption" color="text.secondary">Ordenar por:</Typography>
            <select
              value={serviceOrdersSort}
              onChange={e => setServiceOrdersSort(e.target.value)}
              style={{ fontSize:13, padding:'4px 8px', borderRadius:6, border:'1px solid #e2e8f0', background:'#f8fafc', cursor:'pointer' }}
            >
              <option value="fecha_asc">📅 Fecha (más próxima)</option>
              <option value="fecha_desc">📅 Fecha (más reciente)</option>
              <option value="cliente_asc">👤 Cliente A→Z</option>
              <option value="ciudad_asc">📍 Ciudad A→Z</option>
            </select>
          </Box>
        </Box>

        {serviceOrdersTab === 1 && historial.length > 0 && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Typography variant="caption" color="text.secondary">
              📁 Órdenes de eventos ya realizados. Solo disponible exportar PDF.
            </Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          {listaActiva.map(renderCard)}
          {listaActiva.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {serviceOrdersTab === 0 ? 'No hay órdenes de servicio próximas.' : 'Aún no hay órdenes en el historial.'}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Container>
    );
  };

  /** ===== Órdenes de Bodega — ahora usa BodegaModule ===== */
  const renderWarehouseOrders = () => (
    <BodegaModule orders={warehouseOrders} setOrders={setWarehouseOrders} readOnly={authUser?.role === 'employee'} />
  );

  /** ===== Placeholders ===== */
  const renderPlaceholder = (title) => (
    <Container maxWidth="xl">
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold', color: 'text.primary' }}>{title}</Typography>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">Esta sección está en desarrollo</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          Próximamente funcionalidades de {title.toLowerCase()}
        </Typography>
      </Paper>
    </Container>
  );

 /** ===== Menú lateral ===== */
  // Determinar si el usuario logueado es admin/manager (ve todo) o empleado (ve solo sus módulos)
  // Solo admin y manager tienen acceso total — cualquier otro rol es restringido
  const isAdminUser = authUser && ['admin', 'manager'].includes(authUser.role);
  const userSystemModules = authUser?.systemModules || [];

  // Mapa: sección → módulo requerido (null = siempre visible, '__admin__' = solo admin/manager)
  const SECTION_MODULE_MAP = {
    'dashboard':        null,
    'calendar':         'calendario',
    'ai-assistant':     '__admin__',
    'products':         '__admin__',
    'clients':          'clientes',
    'cotizaciones':     'cotizaciones',
    'service-orders':   'ordenes',
    'warehouse-orders': 'bodega',
    'accounting':       'finanzas',
    'staff':            null,        // todos acceden, el módulo mismo restringe por rol
    'inventory':        'inventario',
    'technical-sheets': 'inventario',
    'reports':          '__admin__',
    'knowledge':        '__admin__',
    'usuarios':         '__admin__',
    'email-marketing':  '__admin__',
    'encuestas':        '__admin__',
    'conversaciones':   '__admin__',
  };

  // Verifica si el usuario tiene acceso a una sección dada
  const canAccessSection = (section) => {
    if (!authUser) return false;
    if (isAdminUser) return true;
    const req = SECTION_MODULE_MAP[section];
    if (req === '__admin__') return false;
    if (req === null) return true;
    return userSystemModules.includes(req);
  };

  const staffText = isAdminUser ? 'Personal' : 'Mi Perfil';

  const menuStructure = [
    { id: 'operacion', label: 'Operación', icon: <DashboardIcon />, items: [
      { text: 'Panel de Control', icon: <DashboardIcon />, section: 'dashboard' },
      { text: 'Calendario', icon: <CalendarIcon />, section: 'calendar' },
      { text: 'Asistente IA', icon: <AIAssistantIcon />, section: 'ai-assistant' },
      { text: 'Alertas', icon: <WarningIcon />, section: 'alerts' },
    ]},
    { id: 'ventas', label: 'Ventas', icon: <QuotesIcon />, items: [
      { text: 'Clientes', icon: <ClientsIcon />, section: 'clients' },
      { text: 'Cotizaciones', icon: <QuotesIcon />, section: 'cotizaciones' },
      { text: 'Bazares', icon: <StoreIcon />, section: 'bazares' },
      { text: 'Órdenes de Servicio', icon: <ServiceOrderIcon />, section: 'service-orders' },
      { text: 'Órdenes de Bodega', icon: <WarehouseOrderIcon />, section: 'warehouse-orders' },
      { text: 'Bonos', icon: <ConfirmationNumberIcon />, section: 'vouchers' },
    ]},
    { id: 'catalogo', label: 'Catálogo', icon: <ProductsIcon />, items: [
      { text: 'Productos', icon: <ProductsIcon />, section: 'products' },
      { text: 'Fichas Técnicas', icon: <TechnicalSheetIcon />, section: 'technical-sheets' },
      { text: 'Imágenes', icon: <ImageIcon />, section: 'imagenes' },
      { text: 'Inventario', icon: <InventoryIcon />, section: 'inventory' },
    ]},
    { id: 'marketing', label: 'Marketing', icon: <CampaignIcon />, items: [
      { text: 'Marketing Digital', icon: <CampaignIcon />, section: 'marketing' },
      { text: 'Landing Pages SEO', icon: <WebIcon />, section: 'landing-pages' },
      { text: 'Inspector SEO', icon: <SearchIcon />, section: 'seo-inspector' },
      { text: 'Email Marketing', icon: <EmailIcon />, section: 'email-marketing' },
      { text: 'WhatsApp', icon: <WhatsAppIcon />, section: 'whatsapp-broadcast' },
      { text: 'Conversaciones', icon: <WhatsAppIcon sx={{ color: '#25d366' }} />, section: 'conversaciones' },
      { text: 'Leads', icon: <HowToRegIcon />, section: 'leads' },
      { text: 'Consultor IA', icon: <HeadsetIcon />, section: 'consultor-ventas' },
      { text: 'Encuestas', icon: <StarIcon />, section: 'encuestas' },
    ]},
    { id: 'finanzas', label: 'Finanzas', icon: <AccountingIcon />, items: [
      { text: 'Contabilidad', icon: <AccountingIcon />, section: 'accounting' },
      { text: 'Reportes', icon: <ReportsIcon />, section: 'reports' },
    ]},
    { id: 'administracion', label: 'Administración', icon: <ManageAccountsIcon />, items: [
      { text: staffText, icon: <ClientsIcon />, section: 'staff' },
      { text: 'Candidatos', icon: <PersonAddIcon />, section: 'candidatos' },
      { text: 'Usuarios', icon: <ManageAccountsIcon />, section: 'usuarios' },
      { text: 'Vehículos', icon: <DirectionsCarIcon />, section: 'vehicles' },
      { text: 'Documentos', icon: <DescriptionIcon />, section: 'legal-docs' },
      { text: 'Configuración', icon: <TuneIcon />, section: 'business-config' },
      { text: 'Diagnóstico', icon: <HealthAndSafetyIcon />, section: 'system-health' },
      { text: 'Contratos', icon: <ContractIcon />, section: 'contratos' },
    ]},
  ];

  // Filtrar items por permisos y eliminar grupos vacíos
  const filteredMenuGroups = menuStructure.map(g => ({
    ...g,
    items: g.items.filter(item => canAccessSection(item.section))
  })).filter(g => g.items.length > 0);

  // Auto-expandir grupo del activeSection actual
  useEffect(() => {
    filteredMenuGroups.forEach(g => {
      if (g.items.some(item => item.section === activeSection)) {
        setMenuGroups(prev => prev[g.id] ? prev : { ...prev, [g.id]: true });
      }
    });
  }, [activeSection]);

// --- FUNCIÓN EXPORTAR PDF CON FILTROS AVANZADOS (CEREBRO 14.0) ---
  const exportCalendarSchedule = () => {
    // 1. Configurar fechas
    const filterStart = new Date(pdfDateRange.start);
    filterStart.setHours(0, 0, 0, 0);
    const filterEnd = new Date(pdfDateRange.end);
    filterEnd.setHours(23, 59, 59, 999);

    // 2. FILTRADO DOBLE: Por Fecha Y Por Tipo
    const eventsToPrint = calendarEvents.filter(ev => {
        // A. Filtro de Fecha
        const evDate = new Date(ev.start);
        const dateOk = evDate >= filterStart && evDate <= filterEnd;
        if (!dateOk) return false;

        // B. Filtro de Tipo (Selector)
        if (pdfReportType === 'events') return ev.type === 'evento';
        if (pdfReportType === 'logistics') return ev.type === 'montaje' || ev.type === 'transporte';
        return true; // 'all' (Muestra todo)
    }).sort((a, b) => new Date(a.start) - new Date(b.start));

    // 3. Generación del PDF
    const doc = new jsPDF();
    let y = 25; 

    // Títulos dinámicos según lo que elegiste
    let reportTitle = "HOJA DE RUTA (COMPLETA)";
    if (pdfReportType === 'events') reportTitle = "REPORTE DE EVENTOS COMERCIALES";
    if (pdfReportType === 'logistics') reportTitle = "ORDEN DE LOGÍSTICA Y TRANSPORTE";

    // Encabezado
    doc.setFillColor(63, 81, 181); 
    doc.rect(0, 0, 210, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text(reportTitle, 105, 12, { align: "center" }); // Título variable
    
    doc.setFontSize(10);
    doc.text(`Del: ${pdfDateRange.start}  Al: ${pdfDateRange.end}`, 105, 18, { align: "center" });

    doc.setTextColor(200, 200, 200);
    doc.setFontSize(8);
    doc.text(`Generado: ${new Date().toLocaleString()}`, 14, 28);
    y += 15;

    if (eventsToPrint.length === 0) {
        doc.setTextColor(0,0,0);
        doc.setFontSize(14);
        doc.text("No se encontraron registros con estos filtros.", 105, 60, {align: "center"});
        doc.save("Reporte_Vacio.pdf");
        setOpenPdfDialog(false);
        return;
    }

    const fmtTime = (isoDate) => {
        if (!isoDate) return '--:--';
        const d = new Date(isoDate);
        return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'America/Bogota' });
    };

    eventsToPrint.forEach((ev) => {
      if (y > 240) { doc.addPage(); y = 20; }

      let phaseTitle = "EVENTO / FIESTA";
      let barColor = [46, 125, 50]; 
      
      if (ev.type === 'montaje') { phaseTitle = "LOGISTICA / MONTAJE"; barColor = [245, 124, 0]; }
      if (ev.type === 'transporte') { phaseTitle = "RUTA / TRANSPORTE"; barColor = [25, 118, 210]; }

      // Caja y Colores
      doc.setDrawColor(200, 200, 200);
      doc.setFillColor(252, 252, 252);
      doc.roundedRect(14, y, 182, 45, 2, 2, 'FD');
      doc.setFillColor(...barColor);
      doc.rect(14, y, 6, 45, 'F'); 

      // Horarios
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("SALIDA / INICIO:", 26, y + 8);
      
      doc.setFontSize(14);
      doc.setTextColor(...barColor);
      doc.text(fmtTime(ev.start), 26, y + 16);

      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text(`Fin: ${fmtTime(ev.end)}`, 26, y + 24);
      doc.setTextColor(0, 0, 0);
      doc.text(new Date(ev.start).toLocaleDateString(), 26, y + 35);

      doc.setDrawColor(220, 220, 220);
      doc.line(68, y + 5, 68, y + 40);

      // Detalles Centrales
      doc.setFontSize(9);
      doc.setTextColor(...barColor);
      doc.setFont("helvetica", "bold");
      doc.text(phaseTitle, 72, y + 7);

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text(ev.title.toUpperCase(), 72, y + 13);

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      
      doc.setFont("helvetica", "bold");
      doc.text("LUGAR:", 72, y + 20);
      doc.setFont("helvetica", "normal");
      doc.text(ev.location || 'Sin ubicacion', 87, y + 20);
      
      const cliente = ev.client || 'No registrado';
      doc.setFont("helvetica", "bold");
      doc.text("CLIENTE:", 72, y + 25);
      doc.setFont("helvetica", "normal");
      doc.text(cliente, 90, y + 25);

      if (ev.coordinator && ev.coordinator.name) {
          doc.setFont("helvetica", "bold");
          doc.text("COORD:", 72, y + 30);
          doc.setFont("helvetica", "normal");
          doc.text(`${ev.coordinator.name} (${ev.coordinator.phone || ''})`, 87, y + 30);
      }

      doc.setFont("helvetica", "bold");
      doc.text("EQUIPOS:", 72, y + 35);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);
      const equipos = ev.equipment && ev.equipment.length > 0 ? ev.equipment.join(', ') : '-';
      const equiposRecortado = equipos.length > 55 ? equipos.substring(0, 55) + '...' : equipos;
      doc.text(equiposRecortado, 90, y + 35);

      // Bloque de Dinero
      if (ev.coordinator && ev.coordinator.balance) {
          doc.setDrawColor(200, 0, 0);
          doc.setFillColor(255, 230, 230);
          doc.roundedRect(135, y + 25, 58, 14, 1, 1, 'FD');

          doc.setTextColor(200, 0, 0);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.text("COBRAR EN SITIO:", 138, y + 30);
          doc.setFontSize(12);
          doc.text(`$ ${Number(ev.coordinator.balance).toLocaleString()}`, 138, y + 36);
      } else {
          doc.setTextColor(46, 125, 50);
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.text("PAGO OK", 145, y + 35);
      }

      y += 50;
    });

    const fileName = `Reporte_${pdfReportType}_${pdfDateRange.start}.pdf`;
    doc.save(fileName);
    setOpenPdfDialog(false);
  };

  // --- MODO CLIENTE (CHAT) ---
  if (isClientMode) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
           <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: 4, height: '90vh' }}>
             <AIAssistant 
               products={products} 
               clients={clients} 
               orders={allOrders} 
             />
           </Paper>
           <Box sx={{ textAlign: 'center', mt: 2 }}>
             <Button href="/" variant="text" size="small" color="secondary">
               Ir al Panel Administrativo
             </Button>
           </Box>
        </Container>
      </ThemeProvider>
    );
  }
  
// Función CEREBRO 15.0: Ciclo Logístico Completo (Ida + Vuelta) + Bloqueo de Stock
  const handleSaveManualEvent = async () => {
    // 1. Validaciones básicas
    if (!newEventForm.title || !newEventForm.date) {
      setSnackbar({ open: true, message: 'Falta título o fecha', severity: 'warning' });
      return;
    }

    const toLocalISO = (d) => {
        const pad = n => n.toString().padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    // --- CÁLCULOS DE TIEMPO ---
    const itemCount = newEventForm.equipment.length > 0 ? newEventForm.equipment.length : 1;
    const minutesPerGameMontaje = 45; 
    
    // CORRECCIÓN DISTANCIA
    const origin = "Chía, Cundinamarca"; 
    const destination = newEventForm.location;
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent("Inflables Da Silva, Chía")}&destination=${encodeURIComponent(destination)}`;

    let finalKm = Number(newEventForm.km) || 0;
    let travelTimeMinutes = 60; 

    // Cálculo automático de ruta si no hay KM manual
    if (finalKm === 0 && window.google && window.google.maps && destination && destination.length > 5) {
      try {
        const service = new window.google.maps.DistanceMatrixService();
        const result = await new Promise((resolve, reject) => {
          service.getDistanceMatrix({
            origins: [origin], destinations: [destination], travelMode: 'DRIVING',
            drivingOptions: { departureTime: new Date(Date.now() + 10000), trafficModel: 'bestguess' }
          }, (response, status) => status === 'OK' ? resolve(response) : reject(status));
        });
        const element = result.rows[0].elements[0];
        if (element.status === 'OK') {
          finalKm = Number((element.distance.value / 1000).toFixed(1));
          const durationSecs = element.duration_in_traffic ? element.duration_in_traffic.value : element.duration.value;
          travelTimeMinutes = Math.ceil((durationSecs / 60) + 30); 
        }
      } catch (e) { travelTimeMinutes = finalKm > 0 ? (finalKm * 1.5) + 30 : 60; }
    } else {
      travelTimeMinutes = finalKm > 0 ? (finalKm * 1.5) + 30 : 60;
    }

    // --- CRONOLOGÍA DE IDA ---
    const eventDateStr = newEventForm.date;
    const eventStartObj = new Date(`${eventDateStr}T${newEventForm.start}:00`);
    const eventEndObj = new Date(`${eventDateStr}T${newEventForm.end}:00`);
    
    const totalMountingMinutes = itemCount * minutesPerGameMontaje;
    
    const montajeStartIdeal = new Date(eventStartObj);
    montajeStartIdeal.setMinutes(montajeStartIdeal.getMinutes() - totalMountingMinutes);
    
    const departureIdeal = new Date(montajeStartIdeal);
    departureIdeal.setMinutes(departureIdeal.getMinutes() - travelTimeMinutes);

    // --- CRONOLOGÍA DE REGRESO (NUEVO) ---
    // El desmontaje dura aprox lo mismo que el montaje
    const dismantlingEnd = new Date(eventEndObj);
    dismantlingEnd.setMinutes(dismantlingEnd.getMinutes() + totalMountingMinutes);

    // El regreso dura lo mismo que la ida
    const returnArrival = new Date(dismantlingEnd);
    returnArrival.setMinutes(returnArrival.getMinutes() + travelTimeMinutes);

    // --- VERIFICACIÓN DE DISPONIBILIDAD TOTAL ---
    // Chequeamos desde que sale el camión (departureIdeal) hasta que regresa a bodega (returnArrival)
    const availability = checkEquipmentAvailability(
        newEventForm.equipment, 
        departureIdeal.toISOString(), 
        returnArrival.toISOString()
    );

    // Si estamos editando un evento existente, ignoramos sus propios conflictos
    if (!newEventForm.id && !availability.available) {
        setSnackbar({ 
            open: true, 
            message: `⚠️ CONFLICTO: No hay stock suficiente para: ${availability.conflicts.join(', ')} en el horario total (incluyendo viajes).`, 
            severity: 'error' 
        });
        return; // Detenemos el guardado
    }

    // --- LIMPIEZA SI ES EDICIÓN ---
    if (newEventForm.id) {
       setCalendarEvents(prev => prev.filter(ev => 
         ev.id !== newEventForm.id && ev.relatedEvent !== newEventForm.id
       ));
    }

    // --- CREACIÓN DE EVENTOS ---
    const mainId = newEventForm.id || `MAN-${Date.now()}`;
    const newEvents = [];
    const fmt = (d) => d.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', hour12: true});
    
    const coordinatorInfo = {
        name: newEventForm.coordinatorName,
        phone: newEventForm.coordinatorPhone,
        balance: newEventForm.balanceDue,
        tieneCoordinador: newEventForm.tieneCoordinador,
        coordinadorVirtual: newEventForm.tieneCoordinador ? null : {
          nombre: newEventForm.coordinadorVirtualNombre,
          telefono: newEventForm.coordinadorVirtualTelefono
        },
        observacionesEntrega: newEventForm.observacionesEntrega || '',
        instructionTemplateId: newEventForm.instructionTemplateId || null
    };

    // 1. Evento Principal (FIESTA)
    newEvents.push({
      id: mainId, title: `${newEventForm.title}`, type: 'evento', 
      client: newEventForm.clientName, 
      coordinator: coordinatorInfo,
      start: toLocalISO(eventStartObj), 
      end: toLocalISO(eventEndObj),
      location: newEventForm.location, 
      equipment: newEventForm.equipment, km: finalKm, mapsUrl: mapsUrl,
      status: 'manual', 
      description: `${newEventForm.notes || ''}`
    });

    // 2. Logística de IDA (Camión + Montaje)
    const detailsIda = `
      🚛 SALIDA BODEGA: ${fmt(departureIdeal)}
      🛠️ INICIO MONTAJE: ${fmt(montajeStartIdeal)}
      🏁 LISTO PARA CLIENTE: ${fmt(eventStartObj)}
    `;
    newEvents.push({
      id: `LG-IDA-${Date.now()}`,
      title: `Logística IDA - ${newEventForm.clientName}`, 
      type: 'montaje', // Color Naranja
      client: newEventForm.clientName, 
      coordinator: coordinatorInfo,
      start: toLocalISO(departureIdeal),
      end: toLocalISO(eventStartObj), 
      location: newEventForm.location,
      equipment: newEventForm.equipment, // ¡Esto bloquea el stock!
      km: finalKm, mapsUrl: mapsUrl,
      description: detailsIda, 
      relatedEvent: mainId
    });

    // 3. Logística de REGRESO (Desmontaje + Camión Vuelta) - ¡NUEVO!
    const detailsVuelta = `
      🔚 FIN EVENTO: ${fmt(eventEndObj)}
      📦 FIN DESMONTAJE: ${fmt(dismantlingEnd)}
      🏠 LLEGADA BODEGA: ${fmt(returnArrival)}
    `;
    newEvents.push({
      id: `LG-VUELTA-${Date.now()}`,
      title: `Logística REGRESO - ${newEventForm.clientName}`, 
      type: 'transporte', // Color Azul
      client: newEventForm.clientName, 
      coordinator: coordinatorInfo,
      start: toLocalISO(eventEndObj),
      end: toLocalISO(returnArrival), 
      location: newEventForm.location,
      equipment: newEventForm.equipment, // ¡Esto sigue bloqueando el stock!
      km: finalKm, mapsUrl: mapsUrl,
      description: detailsVuelta, 
      relatedEvent: mainId
    });
    
    setCalendarEvents(prev => [...prev, ...newEvents]);
    setNewEventDialog(false);
    setNewEventForm({ id: null, title: '', type: 'evento', date: '', start: '10:00', end: '14:00', location: '', clientName: '', coordinatorName: '', coordinatorPhone: '', balanceDue: '', notes: '', equipment: [], km: 0, tieneCoordinador: true, coordinadorVirtualNombre: '', coordinadorVirtualTelefono: '', observacionesEntrega: '', instructionTemplateId: '' });
    setSnackbar({ open: true, message: `Evento agendado. Bloqueo total hasta: ${fmt(returnArrival)}`, severity: 'success' });
  };
  
// Función CEREBRO 4.0: Distancia Visible + Hora Salida Exacta + Link Google Maps

// GUARDIA DE ACCESO — si no hay token y no es ruta pública, mostrar login
  if (!authToken && !isPublicRoute) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginPage onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

// Renderizar formulario público de registro de empleado
if (isEmployeeRegistration) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <EmployeeRegistrationPage />
    </ThemeProvider>
  );
}

// Renderizar página pública de encuesta post-evento
if (isSurveyPage) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SurveyPublicPage />
    </ThemeProvider>
  );
}

// Renderizar Portal de Pagos [Task 9]
if (isPaymentPage) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PaymentPortal />
    </ThemeProvider>
  );
}

// Renderizar QuoteAction si viene de aprobar/modificar desde PDF
if (isQuoteAction) {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QuoteAction />
    </ThemeProvider>
  );
}
 
  /** ===== Layout ===== */
  if (isQuotePublicView) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QuotePublicView 
          quoteNumber={publicQuoteNumber} 
          products={products} 
        />
      </ThemeProvider>
    );
  }
  
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />    
      <Box sx={{ display: 'flex' }}>
        <AppBar position="fixed"
          sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` },
                background: 'linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)' }}>
          <Toolbar>
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(v => !v)} sx={{ mr: 2, display: { sm: 'none' } }}>
              <MenuIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <img src={logoDasilva} alt="Da Silva FunPark" style={{ height: 38, objectFit: 'contain', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,.4))' }} />
            </Box>

            <IconButton color="inherit" onClick={(e) => setNotifAnchor(e.currentTarget)}>
              <Badge badgeContent={notifications.length || 0} color="error"><NotificationsIcon /></Badge>
            </IconButton>
            <Popover open={Boolean(notifAnchor)} anchorEl={notifAnchor} onClose={() => setNotifAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <Box sx={{ width: 340, maxHeight: 400 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
                  <Typography variant="subtitle1" fontWeight={700}>Notificaciones</Typography>
                </Box>
                {notifications.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Sin notificaciones</Typography>
                  </Box>
                ) : notifications.map(n => (
                  <Box key={n.id} sx={{ p: 1.5, borderBottom: '1px solid #f1f5f9', cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}
                    onClick={() => { n.action?.(); setNotifAnchor(null); }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <Typography sx={{ fontSize: 20 }}>{n.icon}</Typography>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{n.text}</Typography>
                        {n.sub && <Typography variant="caption" color="text.secondary">{n.sub}</Typography>}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Popover>
            <IconButton color="inherit" onClick={() => setActiveSection('usuarios')} title="Administrar usuarios">
              <ManageAccountsIcon />
            </IconButton>
            {authUser && (
              <Typography variant="body2" sx={{ mx: 1, opacity: 0.9, display: { xs: 'none', md: 'block' } }}>
                {authUser.firstName || authUser.username}
              </Typography>
            )}
            <IconButton color="inherit" onClick={handleLogout} title="Cerrar sesión">
              <LogoutIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
          {/* Drawer temporario (móvil) */}
          <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}>
            <Box>
              <Toolbar sx={{ justifyContent: 'center' }}>
                <img src={logoDasilva} alt="Da Silva FunPark" style={{ height: 44, objectFit: 'contain' }} />
              </Toolbar>
              <Divider sx={{ borderColor: 'rgba(255,255,255,.2)' }} />
              <List>
                {filteredMenuGroups.map((group) => {
                  const expanded = menuGroups[group.id];
                  return (
                    <Box key={group.id}>
                      <ListItemButton onClick={() => setMenuGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                        sx={{ color: 'white', py: 1.2, '&:hover': { bgcolor: 'rgba(255,255,255,.08)' } }}>
                        <ListItemIcon sx={{ color: 'rgba(255,255,255,.7)', minWidth: 36 }}>{group.icon}</ListItemIcon>
                        <ListItemText primary={group.label} primaryTypographyProps={{ fontWeight: 600 }} />
                        {expanded ? <ExpandLessIcon sx={{ color: 'rgba(255,255,255,.7)' }} /> : <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,.7)' }} />}
                      </ListItemButton>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {group.items.map((item) => (
                            <ListItemButton key={item.text} onClick={() => { setActiveSection(item.section); setMobileOpen(false); }}
                              sx={{ color: 'white', pl: 4, '&:hover': { bgcolor: 'rgba(255,255,255,.1)' },
                                bgcolor: activeSection === item.section ? 'rgba(255,255,255,.2)' : 'transparent' }}>
                              <ListItemIcon sx={{ color: 'white', minWidth: 36 }}>{item.icon}</ListItemIcon>
                              <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            </Box>
          </Drawer>
          {/* Drawer permanente (desktop) */}
          <Drawer variant="permanent"
            sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
            open>
            <Box>
              <Toolbar sx={{ justifyContent: 'center' }}>
                <img src={logoDasilva} alt="Da Silva FunPark" style={{ height: 44, objectFit: 'contain' }} />
              </Toolbar>
              <Divider sx={{ borderColor: 'rgba(255,255,255,.2)' }} />
              <List>
                {filteredMenuGroups.map((group) => {
                  const expanded = menuGroups[group.id];
                  return (
                    <Box key={group.id}>
                      <ListItemButton onClick={() => setMenuGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))}
                        sx={{ color: 'white', py: 1.2, '&:hover': { bgcolor: 'rgba(255,255,255,.08)' } }}>
                        <ListItemIcon sx={{ color: 'rgba(255,255,255,.7)', minWidth: 36 }}>{group.icon}</ListItemIcon>
                        <ListItemText primary={group.label} primaryTypographyProps={{ fontWeight: 600 }} />
                        {expanded ? <ExpandLessIcon sx={{ color: 'rgba(255,255,255,.7)' }} /> : <ExpandMoreIcon sx={{ color: 'rgba(255,255,255,.7)' }} />}
                      </ListItemButton>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {group.items.map((item) => (
                            <ListItemButton key={item.text} onClick={() => { setActiveSection(item.section); setMobileOpen(false); }}
                              sx={{ color: 'white', pl: 4, '&:hover': { bgcolor: 'rgba(255,255,255,.1)' },
                                bgcolor: activeSection === item.section ? 'rgba(255,255,255,.2)' : 'transparent' }}>
                              <ListItemIcon sx={{ color: 'white', minWidth: 36 }}>{item.icon}</ListItemIcon>
                              <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                            </ListItemButton>
                          ))}
                        </List>
                      </Collapse>
                    </Box>
                  );
                })}
              </List>
            </Box>
          </Drawer>
        </Box>

        <Box component="main" sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}>
          <Toolbar />
          <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}><CircularProgress /></Box>}>
          <Toolbar />
          {activeSection === 'dashboard' && renderPanel()}
          {activeSection === 'calendar' && canAccessSection('calendar') && renderCalendar()}
          {activeSection === 'products' && canAccessSection('products') && renderProducts()}
          {activeSection === 'technical-sheets' && canAccessSection('technical-sheets') && <TechnicalSheetsSection
    products={products}
    clients={clients}
    selectedClient={selectedClient}
    handleEditClient={handleEditClient}
    addClientDocument={addClientDocument}
    setOpenClientDialog={setOpenClientDialog}
    setProducts={setProducts}
    setSnackbar={setSnackbar}
    resetClientForm={resetClientForm}
    setActiveSection={setActiveSection}
    editingClientId={editingClientId}
    clientTab={clientTab}
    setClientTab={setClientTab}
    clientForm={clientForm}
    setClientForm={setClientForm}
    removeClientDocument={removeClientDocument}
    handleSaveClient={handleSaveClient}
    handleViewClient={handleViewClient}
    handleDeleteClient={handleDeleteClient}
    openClientDialog={openClientDialog}
  />}
          {activeSection === 'clients' && canAccessSection('clients') && <ClientsModuleComplete />}

          {/* ✅ NUEVO MÓDULO DE COTIZACIONES */}
{(activeSection === 'cotizaciones' || activeSection === 'quotes') && canAccessSection('cotizaciones') && (
  <QuotesModuleNuevo
    products={products}
    clients={clients}
    onCreateOrders={createOrdersFromQuote}
    onDeleteQuote={async (quoteNumber) => {
      if (!quoteNumber) return;
      const toDelete = calendarEvents.filter(ev => ev.quoteNumber === quoteNumber);
      setCalendarEvents(prev => prev.filter(ev => ev.quoteNumber !== quoteNumber));
      setServiceOrders(prev => prev.filter(o => o.quoteNumber !== quoteNumber));
      setWarehouseOrders(prev => prev.filter(o => o.quoteNumber !== quoteNumber));
      for (const ev of toDelete) {
        const mongoId = ev._id || ev.id;
        if (mongoId) { try { await fetch(`/api/calendar/${mongoId}`, { method: 'DELETE' }); } catch (_) {} }
      }
    }}
  />
)}

          {activeSection === 'service-orders' && canAccessSection('service-orders') && renderServiceOrders()}
          {activeSection === 'warehouse-orders' && canAccessSection('warehouse-orders') && renderWarehouseOrders()}

          {/* ═══ ASISTENTE IA UNIFICADO ═══ */}
          {/* Pestaña 1: Centro de Comando (ejecución autónoma) */}
          {/* Pestaña 2: Gestión IA (métricas y config) */}
          {/* Pestaña 3: Simulador de Chat (AIAssistant) */}
{activeSection === 'ai-assistant' && canAccessSection('ai-assistant') && (
  <AIUnifiedSection
    products={products}
    setProducts={setProducts}
    clients={clients}
    setClients={setClients}
    serviceOrders={serviceOrders}
    setServiceOrders={setServiceOrders}
    warehouseOrders={warehouseOrders}
    setWarehouseOrders={setWarehouseOrders}
    calendarEvents={calendarEvents}
    setCalendarEvents={setCalendarEvents}
    createOrdersFromQuote={createOrdersFromQuote}
    setSnackbar={setSnackbar}
    setActiveSection={setActiveSection}
    companyData={companyData}
    allOrders={allOrders}
  />
)}

{activeSection === 'staff' && canAccessSection('staff') && <StaffModule />}

{/* Resto en placeholder (sin Asistente IA) */}
{activeSection === 'accounting' && canAccessSection('accounting') && <AccountingModule />}

{activeSection === 'inventory' && canAccessSection('inventory') && <InventoryModule products={products} />}

{activeSection === 'reports' && canAccessSection('reports') && <ReportsModule />}

{activeSection === 'usuarios' && canAccessSection('usuarios') && <UsersModule />}

{activeSection === 'email-marketing' && canAccessSection('email-marketing') && <EmailMarketingModule authUser={authUser} />}

{activeSection === 'encuestas' && canAccessSection('encuestas') && <SurveyModule authUser={authUser} />}

{activeSection === 'knowledge' && canAccessSection('knowledge') && <AIKnowledgeModule />}

{/* === Módulos reconectados === */}
{activeSection === 'alerts' && canAccessSection('alerts') && <AlertsCenterModule authUser={authUser} />}

{activeSection === 'bazares' && canAccessSection('bazares') && <BazarModule />}

{activeSection === 'vouchers' && canAccessSection('vouchers') && <VouchersModule />}

{activeSection === 'imagenes' && canAccessSection('imagenes') && <ImagenesCatalogoModule />}

{activeSection === 'marketing' && canAccessSection('marketing') && <MarketingModule />}

{activeSection === 'landing-pages' && canAccessSection('landing-pages') && <LandingPagesModule />}

{activeSection === 'seo-inspector' && canAccessSection('seo-inspector') && <SEOInspectorModule />}

{activeSection === 'whatsapp-broadcast' && canAccessSection('whatsapp-broadcast') && <WhatsappBroadcastModule />}

{activeSection === 'conversaciones' && canAccessSection('conversaciones') && <ConversacionesModule />}

{activeSection === 'leads' && canAccessSection('leads') && <LeadsModule />}

{activeSection === 'consultor-ventas' && canAccessSection('consultor-ventas') && <ConsultorVentasModule />}

{activeSection === 'candidatos' && canAccessSection('candidatos') && <CandidatesModule />}

{activeSection === 'vehicles' && canAccessSection('vehicles') && <VehicleModule />}

{activeSection === 'legal-docs' && canAccessSection('legal-docs') && <LegalDocsModule authUser={authUser} />}

{activeSection === 'business-config' && canAccessSection('business-config') && <BusinessConfigModule />}

{activeSection === 'contratos' && canAccessSection('contratos') && <ContratosModule />}

{activeSection === 'system-health' && canAccessSection('system-health') && <SystemHealthModule />}


{/* Centro de Comando ahora integrado como Tab 1 dentro de ai-assistant */}
        </Suspense>
        </Box>
      </Box>

      {/* Dialogs Productos */}
      <Dialog open={openProductDialog} onClose={() => setOpenProductDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Detalles del Producto</Typography>
            <IconButton onClick={() => setOpenProductDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {(() => {
                  const imgs = (selectedProduct.images && selectedProduct.images.length > 0) ? selectedProduct.images : [coverOf(selectedProduct)];
                  const safeIdx = Math.min(detailImageIdx, imgs.length - 1);
                  return (<>
                    <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }} onContextMenu={noDownload}>
                      <CardMedia component="img" height="300" image={imgs[safeIdx]}
                                 alt={selectedProduct.nombre} sx={{ borderRadius: 2, objectFit: 'cover', ...imgProtectSx }} onDragStart={noDownload} />
                      {imgs.length > 1 && (<>
                        <IconButton onClick={() => setDetailImageIdx((safeIdx - 1 + imgs.length) % imgs.length)}
                          sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fff' } }}>
                          <ArrowBackIosNewIcon />
                        </IconButton>
                        <IconButton onClick={() => setDetailImageIdx((safeIdx + 1) % imgs.length)}
                          sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: '#fff' } }}>
                          <ArrowForwardIosIcon />
                        </IconButton>
                        <Box sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', px: 1, py: 0.3, borderRadius: 1, fontSize: '0.75rem' }}>
                          {safeIdx + 1} / {imgs.length}
                        </Box>
                      </>)}
                    </Box>
                    {imgs.length > 1 && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {imgs.map((img, idx) => (
                          <img key={idx} src={img} alt={`img-${idx}`} onClick={() => setDetailImageIdx(idx)}
                               onContextMenu={noDownload} onDragStart={noDownload}
                               style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8, cursor: 'pointer',
                                        border: idx === safeIdx ? '2px solid #1976d2' : '2px solid transparent', userSelect: 'none', WebkitUserDrag: 'none' }} />
                        ))}
                      </Box>
                    )}
                    {selectedProduct.link_youtube && getYouTubeEmbedUrl(selectedProduct.link_youtube) && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Video</Typography>
                        <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 2, overflow: 'hidden' }}>
                          <iframe src={getYouTubeEmbedUrl(selectedProduct.link_youtube)} title="Video del producto"
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        </Box>
                      </Box>
                    )}
                  </>);
                })()}
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>{selectedProduct.nombre}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip label={selectedProduct.categoria} color="primary" />
                  <Chip label={`Código: ${selectedProduct.codigo}`} variant="outlined" />
                </Box>
                
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Precio Alquiler: ${selectedProduct.precio_alquiler_5horas ? Number(selectedProduct.precio_alquiler_5horas).toLocaleString() : 'N/A'}
                </Typography>
                
                {/* Información básica */}
                <Box sx={{ backgroundColor: '#f8fafc', p: 2, borderRadius: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#374151' }}>Especificaciones Básicas</Typography>
                  <Typography variant="body2"><strong>Medidas:</strong> {selectedProduct.medidas || '—'}</Typography>
                  <Typography variant="body2"><strong>Peso del equipo:</strong> {selectedProduct.peso_equipo || '—'} kg</Typography>
                  <Typography variant="body2"><strong>Volumen:</strong> {selectedProduct.volumen_m3 ?? selectedProduct.volumen ?? '—'} m³</Typography>
                  <Typography variant="body2"><strong>Tiempo de armado:</strong> {selectedProduct.tiempo_armado || '—'}</Typography>
                </Box>
                
                {/* Información eléctrica */}
                <Box sx={{ backgroundColor: '#fef3c7', p: 2, borderRadius: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#92400e' }}>Información Eléctrica</Typography>
                  <Typography variant="body2"><strong>Voltaje:</strong> {selectedProduct.voltaje || '—'}</Typography>
                  <Typography variant="body2"><strong>Amperaje:</strong> {selectedProduct.amperaje || '—'} A</Typography>
                  <Typography variant="body2"><strong>Potencia:</strong> {selectedProduct.potencia || '—'} W</Typography>
                </Box>
              </Grid>
              
              {/* Información operativa completa */}
              <Grid item xs={12}>
                <Box sx={{ backgroundColor: '#ecfdf5', p: 2, borderRadius: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#065f46' }}>Información Operativa</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2"><strong>N° operarios:</strong> {selectedProduct.operarios ?? '—'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2"><strong>Capacidad niños:</strong> {selectedProduct.capacidad_ninos || '—'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2"><strong>Capacidad adultos:</strong> {selectedProduct.capacidad_adultos || '—'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2"><strong>Edades permitidas:</strong> {selectedProduct.edades_permitidas || '—'}</Typography>
                    </Grid>
                  </Grid>
                </Box>
                
                {/* Información de instalación */}
                <Box sx={{ backgroundColor: '#fef7ff', p: 2, borderRadius: 2, mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#7c2d12' }}>Requisitos de Instalación</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2"><strong>Área requerida:</strong> {selectedProduct.area_requerida || '—'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2"><strong>Tipo de terreno:</strong> {selectedProduct.terreno || '—'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2"><strong>Viento máx:</strong> {selectedProduct.viento_max || '—'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2"><strong>Anclajes:</strong> {selectedProduct.anclajes || '—'}</Typography>
                    </Grid>
                  </Grid>
                </Box>
                
                {/* Observaciones */}
                {selectedProduct.observaciones && (
                  <Box sx={{ backgroundColor: '#f1f5f9', p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#374151' }}>Observaciones</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedProduct.observaciones}</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProductDialog(false)}>Cerrar</Button>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => {
            setOpenProductDialog(false); handleEditProduct(selectedProduct);
          }}>Editar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingProduct?.uid?.startsWith('NEW-') ? 'Nuevo Producto' : `Editar Producto - ${editingProduct?.codigo}`}</DialogTitle>
        <DialogContent>
          {editingProduct && (
            <Box sx={{ mt: 1 }}>
              <Tabs value={editTab} onChange={(_, v) => setEditTab(v)} sx={{ mb: 2 }}>
                <Tab label="General" /><Tab label="Imágenes" /><Tab label="Documentos" /><Tab label="Videos" />
              </Tabs>
              {editTab === 0 && (
                <Grid container spacing={2}>
                  {/* Información básica */}
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Código" value={editingProduct.codigo || ''} disabled={!editingProduct?.uid?.startsWith('NEW-')} onChange={e => setEditingProduct({ ...editingProduct, codigo: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Nombre" value={editingProduct.nombre || ''} onChange={e => setEditingProduct({ ...editingProduct, nombre: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Categoría</InputLabel>
                      <Select value={editingProduct.categoria || ''} label="Categoría"
                        onChange={e => setEditingProduct({ ...editingProduct, categoria: e.target.value })}>
                        {categories.slice(1).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Precio Alquiler (5h)" type="number" value={editingProduct.precio_alquiler_5horas || ''} onChange={e => setEditingProduct({ ...editingProduct, precio_alquiler_5horas: e.target.value })} /></Grid>
                  {/* ── Jornada Completa ── */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch checked={!!editingProduct.jornada_completa} onChange={e => setEditingProduct({ ...editingProduct, jornada_completa: e.target.checked })} color="primary" />}
                      label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Ofrece alquiler por jornada completa (ej: mañana a noche)</Typography>}
                    />
                  </Grid>
                  {editingProduct.jornada_completa && <>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Horas de jornada completa" type="number" value={editingProduct.horas_jornada || 12}
                        onChange={e => setEditingProduct({ ...editingProduct, horas_jornada: Number(e.target.value) || 12 })}
                        helperText="Ej: 12 horas (mañana a noche)" inputProps={{ min: 1 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Precio jornada completa" type="number" value={editingProduct.precio_jornada || ''}
                        onChange={e => setEditingProduct({ ...editingProduct, precio_jornada: e.target.value })}
                        helperText="Dejar en 0 = mismo precio que estándar"
                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                    </Grid>
                  </>}
                  {/* --- NUEVO: GESTIÓN DE INVENTARIO (Cerebro 16.0) --- */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }}>
                      <Typography variant="subtitle2" color="error">Estado del Inventario</Typography>
                    </Divider>
                  </Grid>
                  
                  <Grid item xs={4}>
                      <TextField fullWidth type="number" label="Stock TOTAL Físico" 
                        value={editingProduct.stock || 1} 
                        onChange={e => setEditingProduct({ ...editingProduct, stock: Number(e.target.value) })} 
                        helperText="Unidades compradas"
                      />
                  </Grid>
                  <Grid item xs={4}>
                      <TextField fullWidth type="number" label="En Mantenimiento" color="warning" focused
                        helperText="Restado del disponible"
                        value={editingProduct.stock_mantenimiento || 0} 
                        onChange={e => setEditingProduct({ ...editingProduct, stock_mantenimiento: Number(e.target.value) })} 
                      />
                  </Grid>
                  <Grid item xs={4}>
                      <TextField fullWidth type="number" label="Dados de Baja" color="error" focused
                        helperText="Dañados definitivamente"
                        value={editingProduct.stock_baja || 0} 
                        onChange={e => setEditingProduct({ ...editingProduct, stock_baja: Number(e.target.value) })} 
                      />
                  </Grid>
                  {/* --------------------------------------------------- */}
                  {/* Especificaciones técnicas detalladas */}
                  <Grid item xs={12}><Divider sx={{ my: 1 }}><Typography variant="subtitle2" color="primary">Especificaciones Técnicas</Typography></Divider></Grid>
                  
                  <Grid item xs={12}><TextField fullWidth label="Medidas (Largo x Ancho x Alto)" value={editingProduct.medidas || ''} onChange={e => setEditingProduct({ ...editingProduct, medidas: e.target.value })} /></Grid>
                  
                  {/* Información eléctrica */}
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel>Voltaje</InputLabel>
                      <Select value={editingProduct.voltaje || ''} label="Voltaje"
                        onChange={e => setEditingProduct({ ...editingProduct, voltaje: e.target.value })}>
                        <MenuItem value="110V">110V</MenuItem>
                        <MenuItem value="220V">220V</MenuItem>
                        <MenuItem value="110V/220V">110V/220V</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth label="Amperaje (A)" type="number" value={editingProduct.amperaje || ''} onChange={e => setEditingProduct({ ...editingProduct, amperaje: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth label="Potencia (W)" type="number" value={editingProduct.potencia || ''} onChange={e => setEditingProduct({ ...editingProduct, potencia: e.target.value })} /></Grid>
                  
                  {/* Información operativa */}
                  <Grid item xs={12} sm={4}><TextField fullWidth type="number" label="Número de operarios" value={editingProduct.operarios ?? ''} onChange={e => setEditingProduct({ ...editingProduct, operarios: Number(e.target.value)||0 })} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth type="number" step="0.1" label="Volumen (m³)" value={editingProduct.volumen_m3 ?? ''} onChange={e => setEditingProduct({ ...editingProduct, volumen_m3: Number(e.target.value)||0 })} /></Grid>
                  <Grid item xs={12} sm={4}><TextField fullWidth type="number" label="Peso equipo (kg)" value={editingProduct.peso_equipo || ''} onChange={e => setEditingProduct({ ...editingProduct, peso_equipo: e.target.value })} /></Grid>
                  
                  {/* Información de uso */}
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Edades permitidas" value={editingProduct.edades_permitidas || ''} onChange={e => setEditingProduct({ ...editingProduct, edades_permitidas: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Capacidad de niños" type="number" value={editingProduct.capacidad_ninos || ''} onChange={e => setEditingProduct({ ...editingProduct, capacidad_ninos: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Capacidad de adultos" type="number" value={editingProduct.capacidad_adultos || ''} onChange={e => setEditingProduct({ ...editingProduct, capacidad_adultos: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Tiempo de armado" value={editingProduct.tiempo_armado || ''} onChange={e => setEditingProduct({ ...editingProduct, tiempo_armado: e.target.value })} /></Grid>
                  
                  {/* Información adicional */}
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Área requerida" value={editingProduct.area_requerida || ''} onChange={e => setEditingProduct({ ...editingProduct, area_requerida: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Tipo de terreno" value={editingProduct.terreno || ''} onChange={e => setEditingProduct({ ...editingProduct, terreno: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Velocidad máxima de viento" value={editingProduct.viento_max || ''} onChange={e => setEditingProduct({ ...editingProduct, viento_max: e.target.value })} /></Grid>
                  <Grid item xs={12} sm={6}><TextField fullWidth label="Tipo de anclajes" value={editingProduct.anclajes || ''} onChange={e => setEditingProduct({ ...editingProduct, anclajes: e.target.value })} /></Grid>
                  
                  <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Observaciones generales" value={editingProduct.observaciones || ''} onChange={e => setEditingProduct({ ...editingProduct, observaciones: e.target.value })} /></Grid>
                </Grid>
              )}
              {editTab === 1 && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Button variant="outlined" startIcon={<PhotoIcon />} component="label">
                      Cargar archivo
                      <input hidden accept="image/*" multiple type="file" onChange={addImages} />
                    </Button>
                    <TextField size="small" placeholder="Pegar URL de imagen (https://...)" value={imageUrlInput}
                      onChange={e => setImageUrlInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addImageByUrl()}
                      sx={{ flex: 1, minWidth: 200 }}
                      InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={addImageByUrl} disabled={!imageUrlInput.trim()}><AddIcon /></IconButton></InputAdornment> }}
                    />
                  </Box>
                  <Grid container spacing={2}>
                    {(editingProduct.images || []).map((url, idx) => {
                      const isCover = editingProduct.coverIndex === idx;
                      const isTech = editingProduct.techPhotoUrl === url;
                      return (
                        <Grid item key={idx} xs={6} sm={4} md={3}>
                          <Box sx={{ border:'1px solid #e2e8f0', borderRadius:2, overflow:'hidden' }}>
                            <img src={url} alt={`img-${idx}`} style={{ width:'100%', height:150, objectFit:'cover' }} />
                            <Box sx={{ p:1, display:'flex', gap:.5, justifyContent:'space-between', bgcolor:'#f8fafc' }}>
                              <Button size="small" variant={isCover ? 'contained' : 'outlined'} onClick={() => setCover(idx)}>{isCover ? 'Portada' : 'Hacer portada'}</Button>
                              <Button size="small" variant={isTech ? 'contained' : 'outlined'} onClick={() => setTechPhoto(url)}>{isTech ? 'Ficha' : 'Usar en ficha'}</Button>
                              <Button size="small" color="error" variant="outlined" onClick={() => removeImage(idx)}>Eliminar</Button>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}
              {editTab === 2 && (
                <Box>
                  <Button variant="outlined" startIcon={<AttachIcon />} component="label" sx={{ mb: 2 }}>
                    Cargar documentos
                    <input hidden accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" multiple type="file" onChange={addDocuments} />
                  </Button>
                  <Box sx={{ display:'grid', gap:.5 }}>
                    {(editingProduct.documents || []).map((f, i) => (
                      <Box key={i} sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', p:.75, px:1.25, bgcolor:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:2 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: '75%' }}>📄 {f.name || '(archivo)'}</Typography>
                        <Button size="small" color="error" variant="outlined" onClick={() => removeDocument(i)}>Eliminar</Button>
                      </Box>
                    ))}
                    {(!editingProduct.documents || editingProduct.documents.length === 0) && <Typography variant="body2" color="text.secondary">Sin documentos</Typography>}
                  </Box>
                </Box>
              )}
              {editTab === 3 && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    <Button variant="outlined" startIcon={<VideoIcon />} component="label">
                      Cargar archivo
                      <input hidden accept="video/*" multiple type="file" onChange={addVideos} />
                    </Button>
                    <TextField size="small" placeholder="Pegar URL de video o YouTube (https://...)" value={videoUrlInput}
                      onChange={e => setVideoUrlInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addVideoByUrl()}
                      sx={{ flex: 1, minWidth: 200 }}
                      InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={addVideoByUrl} disabled={!videoUrlInput.trim()}><AddIcon /></IconButton></InputAdornment> }}
                    />
                  </Box>
                  <Box sx={{ display:'grid', gap:.5 }}>
                    {(editingProduct.videos || []).map((f, i) => {
                      const vidUrl = typeof f === 'string' ? f : f.url;
                      const vidName = typeof f === 'string' ? f : f.name;
                      const isYoutube = vidUrl && (vidUrl.includes('youtu.be') || vidUrl.includes('youtube.com'));
                      return (
                        <Box key={i} sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', p:.75, px:1.25, bgcolor:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: '75%' }}>
                            <Typography variant="body2" noWrap>{isYoutube ? '▶️' : '🎬'} {vidName || '(video)'}</Typography>
                            {vidUrl && <IconButton size="small" onClick={() => window.open(vidUrl, '_blank')} title="Abrir"><LaunchIcon fontSize="small" /></IconButton>}
                          </Box>
                          <Button size="small" color="error" variant="outlined" onClick={() => removeVideo(i)}>Eliminar</Button>
                        </Box>
                      );
                    })}
                    {(!editingProduct.videos || editingProduct.videos.length === 0) && <Typography variant="body2" color="text.secondary">Sin videos</Typography>}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancelar</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveEdit}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogos de Calendario */}

      {/* Diálogo: todos los eventos de un día */}
      <Dialog open={openDayDialog} onClose={() => setOpenDayDialog(false)} maxWidth="sm" fullWidth>
        {dayDialogDate && (() => {
          const { year, month, day } = dayDialogDate;
          const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
          const dayEvs = calendarEvents.filter(ev => {
            const d = new Date(ev.start);
            return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
          }).sort((a, b) => new Date(a.start) - new Date(b.start));
          const typeColors = { evento: '#047857', montaje: '#b45309', transporte: '#1d4ed8', otro: '#6b7280' };
          const typeBg    = { evento: '#ecfdf5', montaje: '#fffbeb', transporte: '#eff6ff', otro: '#f9fafb' };
          const typeBorder = { evento: '#a7f3d0', montaje: '#fde68a', transporte: '#bfdbfe', otro: '#e5e7eb' };
          const typeEmoji  = { evento: '🎪', montaje: '🔧', transporte: '🚛', otro: '📌' };
          return (<>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {day} de {monthNames[month]} {year}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {dayEvs.length} evento{dayEvs.length !== 1 ? 's' : ''} programado{dayEvs.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={() => { setOpenDayDialog(false); setNewEventDialog(true); }}
                    sx={{ textTransform: 'none', fontSize: 12 }}>
                    Nuevo evento
                  </Button>
                  <IconButton size="small" onClick={() => setOpenDayDialog(false)}><CloseIcon /></IconButton>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
              {dayEvs.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">No hay eventos programados para este día.</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  {dayEvs.map((ev, idx) => {
                    const startFmt = ev.start ? new Date(ev.start).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Bogota' }) : '';
                    const endFmt   = ev.end   ? new Date(ev.end).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Bogota' }) : '';
                    const col  = typeColors[ev.type] || typeColors.otro;
                    const bg   = typeBg[ev.type]    || typeBg.otro;
                    const bdr  = typeBorder[ev.type] || typeBorder.otro;
                    const emoji = typeEmoji[ev.type] || typeEmoji.otro;
                    return (
                      <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: bdr, bgcolor: bg, cursor: 'pointer',
                        '&:hover': { filter: 'brightness(0.97)', borderColor: col } }}
                        onClick={() => { setSelectedEvent(ev); setOpenEventDialog(true); setOpenDayDialog(false); }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Typography sx={{ fontSize: 22, lineHeight: 1.2, mt: 0.2 }}>{emoji}</Typography>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: col, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {ev.title}
                              </Typography>
                              {startFmt && (
                                <Typography variant="caption" sx={{ color: '#64748b', whiteSpace: 'nowrap', fontWeight: 600 }}>
                                  {startFmt}{endFmt ? ` - ${endFmt}` : ''}
                                </Typography>
                              )}
                            </Box>
                            {ev.client && <Typography variant="caption" sx={{ color: '#475569', display: 'block' }}>👤 {ev.client}</Typography>}
                            {ev.location && <Typography variant="caption" sx={{ color: '#475569', display: 'block' }}>📍 {ev.location}</Typography>}
                            {ev.quoteNumber && <Typography variant="caption" sx={{ color: '#6366f1', display: 'block', fontWeight: 600 }}>COT: {ev.quoteNumber}</Typography>}
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </DialogContent>
          </>);
        })()}
      </Dialog>

     {/* Diálogo para ver detalles del evento y EDITAR (CORREGIDO) */}
      <Dialog open={openEventDialog} onClose={() => setOpenEventDialog(false)} maxWidth="md" fullWidth
        fullScreen={window.innerWidth < 600}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Detalles del Evento</Typography>
            <IconButton onClick={() => setOpenEventDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
       <DialogContent>
          {selectedEvent && (
            <Grid container spacing={2}>
              {/* CABECERA: ETIQUETAS Y BOTÓN MAPA */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent:'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={selectedEvent.type === 'evento' ? '🎪 FIESTA' : selectedEvent.type === 'montaje' ? '🛠️ LOGÍSTICA IDA' : '🚛 LOGÍSTICA REGRESO'} 
                          color={selectedEvent.type === 'evento' ? 'success' : selectedEvent.type === 'montaje' ? 'warning' : 'primary'} />
                    <Chip label={selectedEvent.status || 'Programado'} variant="outlined" />
                    {selectedEvent.quoteNumber && (
                      <Chip label={`Cotización: ${selectedEvent.quoteNumber}`} size="small" variant="outlined" color="secondary" />
                    )}
                    {selectedEvent.orderServiceId && (
                      <Chip label={`OS: ${selectedEvent.orderServiceId}`} size="small" variant="outlined" color="info" />
                    )}
                  </Box>
                  
                  {/* BOTONES DE ACCIÓN */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {selectedEvent.mapsUrl && (
                      <Button variant="contained" color="primary" size="small" href={selectedEvent.mapsUrl} target="_blank" startIcon={<SearchIcon />}>
                        Ver Mapa
                      </Button>
                    )}
                    {selectedEvent.clientPhone && (
                      <Button variant="outlined" color="success" size="small" href={`https://wa.me/57${selectedEvent.clientPhone?.replace(/\D/g, '')}`} target="_blank" startIcon={<WhatsAppIcon />}>
                        WhatsApp
                      </Button>
                    )}
                  </Box>
                </Box>
              </Grid>
              
              {/* COLUMNA IZQUIERDA: CLIENTE Y COORDINADOR */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>{selectedEvent.title}</Typography>
                
                {/* 1. CAJA AZUL: CLIENTE (QUIEN PAGA) */}
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#f0f9ff', borderColor: '#bae6fd' }}>
                  <Typography variant="caption" color="text.secondary" sx={{fontWeight:'bold'}}>CLIENTE / CONTRATANTE</Typography>
                  <Typography variant="subtitle1" sx={{ color: '#0369a1', fontWeight: 'bold' }}>
                    {selectedEvent.client || 'No registrado'}
                  </Typography>
                  {selectedEvent.clientPhone && (
                    <Typography variant="body2" sx={{ color: '#0369a1' }}>
                      📞 {selectedEvent.clientPhone}
                    </Typography>
                  )}
                  {selectedEvent.clientEmail && (
                    <Typography variant="body2" sx={{ color: '#0369a1' }}>
                      ✉️ {selectedEvent.clientEmail}
                    </Typography>
                  )}
                </Paper>

                {/* 2. CAJA VERDE: COORDINADOR (QUIEN RECIBE) */}
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#f0fdf4', borderColor: '#86efac' }}>
                  <Typography variant="caption" color="text.secondary" sx={{fontWeight:'bold'}}>COORDINADOR EN SITIO</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {selectedEvent.coordinator?.name || 'Mismo cliente'}
                        </Typography>
                        <Typography variant="body2">
                            {selectedEvent.coordinator?.phone || selectedEvent.clientPhone || 'Sin teléfono'}
                        </Typography>
                    </Box>
                    {/* ALERTA DE COBRO (SI HAY SALDO PENDIENTE) */}
                    {(selectedEvent.coordinator?.balance > 0 || selectedEvent.financials?.balanceDue > 0) && (
                        <Chip label={`Cobrar: $${Number(selectedEvent.coordinator?.balance || selectedEvent.financials?.balanceDue || 0).toLocaleString()}`} 
                              color="error" sx={{fontWeight:'bold', height: 'auto', py: 0.5}} />
                    )}
                  </Box>
                </Paper>

                {/* UBICACIÓN CON LINK */}
                <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#fefce8', borderColor: '#fde68a' }}>
                  <Typography variant="caption" color="text.secondary" sx={{fontWeight:'bold'}}>📍 UBICACIÓN</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedEvent.location || '—'}</Typography>
                  {selectedEvent.km > 0 && (
                    <Typography variant="body2" sx={{ color: '#e11d48', fontWeight: 'bold' }}>
                      🛣️ Distancia: {selectedEvent.km} km · Transporte: ${(selectedEvent.km * 3000).toLocaleString()} COP
                    </Typography>
                  )}
                  {selectedEvent.mapsUrl && (
                    <Button size="small" href={selectedEvent.mapsUrl} target="_blank" sx={{ mt: 0.5, textTransform: 'none' }}>
                      🗺️ Abrir en Google Maps
                    </Button>
                  )}
                </Paper>
              </Grid>
              
              {/* COLUMNA DERECHA: TIEMPOS Y EQUIPOS */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 1.5, border: '1px dashed #cbd5e1', borderRadius: 1, mb: 2 }}>
                  {(() => {
                    // Leer hora directamente del string ISO sin conversión de timezone
                    // Las fechas en MongoDB están en UTC. Colombia = UTC-5 (sin horario de verano)
                    const fmtISO = (iso) => {
                      if (!iso) return '--';
                      const dt = new Date(iso);
                      if (isNaN(dt.getTime())) return '--';
                      const h = ((dt.getUTCHours() - 5) + 24) % 24;
                      const min = dt.getUTCMinutes();
                      const ampm = h >= 12 ? 'PM' : 'AM';
                      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                      return `${h12}:${String(min).padStart(2, '0')} ${ampm}`;
                    };
                    const addMinsISO = (iso, addMins) => {
                      if (!iso) return '--';
                      const dt = new Date(iso);
                      if (isNaN(dt.getTime())) return '--';
                      let total = ((dt.getUTCHours() - 5 + 24) % 24) * 60 + dt.getUTCMinutes() + addMins;
                      total = ((total % 1440) + 1440) % 1440;
                      const h = Math.floor(total / 60);
                      const min = total % 60;
                      const ampm = h >= 12 ? 'PM' : 'AM';
                      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                      return `${h12}:${String(min).padStart(2, '0')} ${ampm}`;
                    };
                    const dateFromISO = (iso) => {
                      if (!iso) return '';
                      const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
                      if (!m) return '';
                      const dt = new Date(Date.UTC(+m[1], +m[2]-1, +m[3]));
                      return dt.toLocaleDateString('es-CO', { weekday:'long', day:'numeric', month:'long', year:'numeric', timeZone:'UTC' });
                    };

                    if (selectedEvent.type === 'montaje') {
                      // Buscar el evento de fiesta relacionado para mostrar su hora exacta
                      const relEvento = calendarEvents.find(ev =>
                        ev.type === 'evento' && (
                          ev.id === selectedEvent.relatedEvent ||
                          ev.id === selectedEvent.parentEventId ||
                          (selectedEvent.quoteNumber && ev.quoteNumber === selectedEvent.quoteNumber)
                        )
                      );
                      if (relEvento) {
                        return (<>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>🎪 HORARIO FIESTA:</Typography>
                          <Typography variant="h6" color="primary">{fmtISO(relEvento.start)} — {fmtISO(relEvento.end)}</Typography>
                          <Typography variant="body2" color="text.secondary">📅 {dateFromISO(relEvento.start)}</Typography>
                        </>);
                      }
                      // Fallback: calcular desde ventana logística
                      const km = selectedEvent.km || 0;
                      const travelMin = km > 0 ? Math.round(km * 1.5 + 30) : 60;
                      const itemCount = (selectedEvent.equipment || []).length || 1;
                      const setupMin = Math.max(45, itemCount * 45);
                      return (<>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>🎪 HORARIO FIESTA (estimado):</Typography>
                        <Typography variant="h6" color="primary">
                          {addMinsISO(selectedEvent.start, travelMin + setupMin)} — {addMinsISO(selectedEvent.end, -(travelMin + setupMin))}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">📅 {dateFromISO(selectedEvent.start)}</Typography>
                      </>);
                    }
                    return (<>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>⏰ HORARIO FIESTA:</Typography>
                      <Typography variant="h6" color="primary">{fmtISO(selectedEvent.start)} — {fmtISO(selectedEvent.end)}</Typography>
                      <Typography variant="body2" color="text.secondary">📅 {dateFromISO(selectedEvent.start)}</Typography>
                    </>);
                  })()}
                </Box>

                {/* EQUIPOS */}
                {selectedEvent.equipment && selectedEvent.equipment.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>🎪 Equipos ({selectedEvent.equipment.length}):</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {selectedEvent.equipment.map((eq, idx) => (
                        <Chip key={idx} 
                          label={selectedEvent.equipmentNames?.[idx] ? `${eq} - ${selectedEvent.equipmentNames[idx]}` : eq} 
                          size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* DATOS FINANCIEROS */}
                {selectedEvent.financials && selectedEvent.financials.total > 0 && (
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#f0fdf4', borderColor: '#86efac' }}>
                    <Typography variant="caption" color="text.secondary" sx={{fontWeight:'bold'}}>💰 DATOS FINANCIEROS</Typography>
                    <Typography variant="body2">Subtotal: ${Number(selectedEvent.financials.subtotal || 0).toLocaleString()}</Typography>
                    <Typography variant="body2">Transporte: ${Number(selectedEvent.financials.transport || 0).toLocaleString()}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#059669' }}>
                      Total: ${Number(selectedEvent.financials.total || 0).toLocaleString()}
                    </Typography>
                  </Paper>
                )}
                
                {/* DESGLOSE DETALLADO DE LOGÍSTICA — lee hora directo del ISO sin timezone */}
                {selectedEvent.type === 'montaje' && selectedEvent.start && (() => {
                  const addM = (iso, addMins) => {
                    const m = String(iso).match(/T(\d{2}):(\d{2})/);
                    if (!m) return '--';
                    let total = parseInt(m[1]) * 60 + parseInt(m[2]) + addMins;
                    total = ((total % 1440) + 1440) % 1440;
                    const h = Math.floor(total / 60);
                    const min = total % 60;
                    return `${String(h).padStart(2,'0')}:${String(min).padStart(2,'0')}`;
                  };
                  const fmtRaw = (iso) => {
                    const m = String(iso).match(/T(\d{2}):(\d{2})/);
                    return m ? `${m[1]}:${m[2]}` : '--';
                  };
                  const km        = selectedEvent.km || 0;
                  const travelMin = km > 0 ? Math.round(km * 1.5 + 30) : 60;
                  const itemCount = (selectedEvent.equipment || []).length || 1;
                  const setupMin  = Math.max(45, itemCount * 45);
                  const logStartStr  = selectedEvent.start;
                  const logEndStr    = selectedEvent.end;
                  return (
                    <Paper variant="outlined" sx={{ mt: 1, p: 2, bgcolor: '#fff7ed', borderLeft: '4px solid #f97316' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#c2410c', mb: 1 }}>⏱️ Cronograma del día:</Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 12px', fontFamily: 'monospace', fontSize: 13 }}>
                        <span style={{color:'#64748b'}}>🚛 Salida bodega</span>   <strong>{fmtRaw(logStartStr)}</strong>
                        <span style={{color:'#64748b'}}>🔧 Inicio montaje</span>  <strong>{addM(logStartStr, travelMin)}</strong>
                        <span style={{color:'#64748b'}}>🎪 Inicio evento</span>   <strong>{addM(logStartStr, travelMin + setupMin)}</strong>
                        <span style={{color:'#64748b'}}>🏁 Fin evento</span>      <strong>{addM(logEndStr, -(travelMin + setupMin))}</strong>
                        <span style={{color:'#64748b'}}>📦 Fin desmontaje</span>  <strong>{addM(logEndStr, -travelMin)}</strong>
                        <span style={{color:'#64748b'}}>🏠 Llegada bodega</span>  <strong>{fmtRaw(logEndStr)}</strong>
                        {km > 0 && <><span style={{color:'#64748b'}}>📏 Distancia</span><strong>{km} km · ~{travelMin} min viaje · {setupMin} min montaje</strong></>}
                      </Box>
                    </Paper>
                  );
                })()}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEventDialog(false)}>Cerrar</Button>
          
          {/* BOTÓN EDITAR QUE AHORA SÍ FUNCIONA */}
          {selectedEvent?.type === 'evento' && (
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => {
              // Cargamos los datos del evento al formulario de edición
              const startD = new Date(selectedEvent.start);
              const endD = new Date(selectedEvent.end);
              
              setNewEventForm({
                id: selectedEvent.id, // Importante: pasamos el ID para editar
                title: selectedEvent.title,
                clientName: selectedEvent.client || '',
                type: 'evento',
                date: startD.toISOString().slice(0, 10),
                start: startD.toLocaleTimeString('es-CO', {hour: '2-digit', minute:'2-digit', hour12: false, timeZone: 'America/Bogota'}),
                end: endD.toLocaleTimeString('es-CO', {hour: '2-digit', minute:'2-digit', hour12: false, timeZone: 'America/Bogota'}),
                location: selectedEvent.location || '',
                equipment: selectedEvent.equipment || [],
                km: selectedEvent.km || 0, // Pasamos los KM
                notes: selectedEvent.description || ''
              });

              // Cerramos esta ventana y abrimos la de edición
              setOpenEventDialog(false);
              setNewEventDialog(true);
            }}>
              Editar y Reprogramar
            </Button>
          )}

          <Button variant="outlined" color="error" onClick={async () => {
             // Borrar del state local
             const idsToRemove = [];
             calendarEvents.forEach(ev => {
               if (ev.id === selectedEvent.id || ev.relatedEvent === selectedEvent.id) idsToRemove.push(ev);
             });
             setCalendarEvents(prev => prev.filter(ev => ev.id !== selectedEvent.id && ev.relatedEvent !== selectedEvent.id));
             setOpenEventDialog(false);
             setSnackbar({ open: true, message: 'Evento eliminado', severity: 'success' });
             // Borrar de MongoDB también
             for (const ev of idsToRemove) {
               const mongoId = ev._id || ev.id;
               if (mongoId) {
                 try { await fetch(`/api/calendar/${mongoId}`, { method: 'DELETE' }); } catch (_) {}
               }
             }
          }}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para crear nuevo evento */}
      
      {/* --- INICIO DEL NUEVO DIÁLOGO (PARTE 3) --- */}
     {/* --- VENTANA INTELIGENTE (EQUIPOS + KM) --- */}
      <Dialog open={newEventDialog} onClose={() => setNewEventDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Crear Evento con Logística Automática</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Título del Evento" 
                value={newEventForm.title} onChange={e => setNewEventForm({...newEventForm, title: e.target.value})}
              />
            </Grid>
           <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Nombre del Cliente / Empresa" 
                 value={newEventForm.clientName} onChange={e => setNewEventForm({...newEventForm, clientName: e.target.value})}
              />
            </Grid>
            {/* --- BLOQUE COORDINADOR Y COBRO --- */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newEventForm.tieneCoordinador}
                    onChange={e => setNewEventForm({ ...newEventForm, tieneCoordinador: e.target.checked })}
                    color="primary"
                  />
                }
                label={newEventForm.tieneCoordinador ? 'Hay coordinador en sitio' : 'Sin coordinador — instrucciones automáticas por WhatsApp'}
              />
            </Grid>

            {newEventForm.tieneCoordinador ? (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Coordinador en Sitio (Nombre)"
                    value={newEventForm.coordinatorName} onChange={e => setNewEventForm({...newEventForm, coordinatorName: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Teléfono Coordinador"
                    value={newEventForm.coordinatorPhone} onChange={e => setNewEventForm({...newEventForm, coordinatorPhone: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Saldo a Cobrar ($)" type="number" color="warning" focused
                    helperText="Si aplica"
                    value={newEventForm.balanceDue} onChange={e => setNewEventForm({...newEventForm, balanceDue: e.target.value})}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Coordinador Virtual (Nombre)"
                    helperText="Persona a cargo de forma remota"
                    value={newEventForm.coordinadorVirtualNombre}
                    onChange={e => setNewEventForm({...newEventForm, coordinadorVirtualNombre: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Teléfono Coordinador Virtual"
                    value={newEventForm.coordinadorVirtualTelefono}
                    onChange={e => setNewEventForm({...newEventForm, coordinadorVirtualTelefono: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Saldo a Cobrar ($)" type="number" color="warning" focused
                    helperText="Si aplica"
                    value={newEventForm.balanceDue} onChange={e => setNewEventForm({...newEventForm, balanceDue: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <FormControl fullWidth>
                    <InputLabel>Plantilla de instrucciones</InputLabel>
                    <Select
                      value={newEventForm.instructionTemplateId}
                      label="Plantilla de instrucciones"
                      onChange={e => setNewEventForm({...newEventForm, instructionTemplateId: e.target.value})}
                    >
                      <MenuItem value=""><em>Sin plantilla</em></MenuItem>
                      {instructionTemplates.map(t => (
                        <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={2}
                    label="Observaciones de entrega"
                    placeholder="Ej: El portero se llama Juan, puerta lateral, parqueadero interno..."
                    value={newEventForm.observacionesEntrega}
                    onChange={e => setNewEventForm({...newEventForm, observacionesEntrega: e.target.value})}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                    Al guardar, el sistema enviará las instrucciones por WhatsApp a cada operario asignado.
                  </Alert>
                </Grid>
              </>
            )}
      
            {/* SELECTOR DE EQUIPOS IMPORTANTE */}
           {/* SELECTOR DE EQUIPOS (CORREGIDO Y BLINDADO) */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Seleccionar Equipos (Para calcular tiempos)</InputLabel>
                <Select
                  multiple
                  value={newEventForm.equipment}
                  label="Seleccionar Equipos (Para calcular tiempos)"
                  onChange={(e) => {
                    const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setNewEventForm({...newEventForm, equipment: val});
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => <Chip key={value} label={value} size="small" />)}
                    </Box>
                  )}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                >
                  {products.map((prod) => (
                    <MenuItem 
                      key={prod.uid} 
                      /* AQUÍ ESTÁ EL ARREGLO: Usamos el ID si no hay código */
                      value={prod.codigo || prod.uid} 
                      style={{ 
                        fontWeight: newEventForm.equipment.includes(prod.codigo || prod.uid) ? 'bold' : 'normal',
                        backgroundColor: newEventForm.equipment.includes(prod.codigo || prod.uid) ? '#eff6ff' : 'transparent'
                      }}
                    >
                      {prod.codigo ? `${prod.codigo} - ` : ''}{prod.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="date" label="Fecha del Evento" InputLabelProps={{ shrink: true }} 
                value={newEventForm.date} onChange={e => setNewEventForm({...newEventForm, date: e.target.value})}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type="time" label="Inicio Fiesta" InputLabelProps={{ shrink: true }} 
                value={newEventForm.start} onChange={e => setNewEventForm({...newEventForm, start: e.target.value})}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth type="time" label="Fin Fiesta" InputLabelProps={{ shrink: true }} 
                value={newEventForm.end} onChange={e => setNewEventForm({...newEventForm, end: e.target.value})}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Ubicación" 
                value={newEventForm.location} onChange={e => setNewEventForm({...newEventForm, location: e.target.value})}
              />
            </Grid>
            {/* CAMPO DE KILÓMETROS */}
            <Grid item xs={12} sm={4}>
              <TextField fullWidth type="number" label="Distancia (KM)" helperText="Calcula hora salida"
                value={newEventForm.km} onChange={e => setNewEventForm({...newEventForm, km: Number(e.target.value)})}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notas" 
                value={newEventForm.notes} onChange={e => setNewEventForm({...newEventForm, notes: e.target.value})}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                Al guardar, el sistema calculará automáticamente:<br/>
                <b>1. Hora salida camión</b> (según KM)<br/>
                <b>2. Hora inicio montaje</b> (según cantidad de equipos)<br/>
                <b>3. Hora inicio evento</b>
              </Alert>
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewEventDialog(false)}>Cancelar</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveManualEvent}>
            Programar Automáticamente
          </Button>
        </DialogActions>
      </Dialog>
      {/* --- FIN DEL NUEVO DIÁLOGO --- */}

      {/* --- DIÁLOGO EDITAR ORDEN DE SERVICIO / BODEGA --- */}
      <Dialog open={!!editingOrder} onClose={() => setEditingOrder(null)} maxWidth="md" fullWidth>
        <DialogTitle>{editOrderType === 'service' ? 'Editar Orden de Servicio' : 'Editar Orden de Bodega'} — {editingOrder?.id}</DialogTitle>
        <DialogContent>
          {editingOrder && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Cliente" value={editingOrder.client?.name || ''} onChange={e => setEditingOrder(prev => ({ ...prev, client: { ...prev.client, name: e.target.value } }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Teléfono" value={editingOrder.client?.phone || ''} onChange={e => setEditingOrder(prev => ({ ...prev, client: { ...prev.client, phone: e.target.value } }))} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Fecha" type="date" InputLabelProps={{ shrink: true }} value={editingOrder.date || editingOrder.event?.date || ''} onChange={e => setEditingOrder(prev => ({ ...prev, date: e.target.value, event: { ...prev.event, date: e.target.value } }))} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Hora inicio" type="time" InputLabelProps={{ shrink: true }} value={editingOrder.event?.start || ''} onChange={e => setEditingOrder(prev => ({ ...prev, event: { ...prev.event, start: e.target.value } }))} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Hora fin" type="time" InputLabelProps={{ shrink: true }} value={editingOrder.event?.end || ''} onChange={e => setEditingOrder(prev => ({ ...prev, event: { ...prev.event, end: e.target.value } }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Ciudad" value={editingOrder.event?.city || editingOrder.destination?.city || ''} onChange={e => setEditingOrder(prev => ({ ...prev, event: { ...prev.event, city: e.target.value }, destination: { ...prev.destination, city: e.target.value } }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Dirección" value={editingOrder.event?.address || editingOrder.destination?.address || ''} onChange={e => setEditingOrder(prev => ({ ...prev, event: { ...prev.event, address: e.target.value }, destination: { ...prev.destination, address: e.target.value } }))} />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Productos ({(editingOrder.items || []).length})</Typography>
                {(editingOrder.items || []).map((item, idx) => (
                  <Grid container spacing={1} key={idx} sx={{ mb: 1 }}>
                    <Grid item xs={5}><TextField fullWidth size="small" label="Producto" value={item.name || item.productName || ''} onChange={e => { const items = [...(editingOrder.items || [])]; items[idx] = { ...items[idx], name: e.target.value, productName: e.target.value }; setEditingOrder(prev => ({ ...prev, items })); }} /></Grid>
                    <Grid item xs={2}><TextField fullWidth size="small" label="Código" value={item.code || item.productCode || ''} onChange={e => { const items = [...(editingOrder.items || [])]; items[idx] = { ...items[idx], code: e.target.value, productCode: e.target.value }; setEditingOrder(prev => ({ ...prev, items })); }} /></Grid>
                    <Grid item xs={2}><TextField fullWidth size="small" label="Cant." type="number" value={item.qty || item.quantity || 1} onChange={e => { const items = [...(editingOrder.items || [])]; items[idx] = { ...items[idx], qty: Number(e.target.value) || 1, quantity: Number(e.target.value) || 1 }; setEditingOrder(prev => ({ ...prev, items })); }} /></Grid>
                    <Grid item xs={2}><TextField fullWidth size="small" label="Precio" type="number" value={item.unitPrice || item.price || ''} onChange={e => { const items = [...(editingOrder.items || [])]; items[idx] = { ...items[idx], unitPrice: Number(e.target.value) || 0, price: Number(e.target.value) || 0 }; setEditingOrder(prev => ({ ...prev, items })); }} /></Grid>
                    <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}><IconButton size="small" color="error" onClick={() => { const items = (editingOrder.items || []).filter((_, i) => i !== idx); setEditingOrder(prev => ({ ...prev, items })); }}><DeleteIcon fontSize="small" /></IconButton></Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth size="small" label="Accesorios (uno por línea)" multiline rows={1} 
                        value={(item.accesorios || []).join('\n')}
                        onChange={e => { const items = [...(editingOrder.items || [])]; items[idx] = { ...items[idx], accesorios: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) }; setEditingOrder(prev => ({ ...prev, items })); }}
                      />
                    </Grid>
                  </Grid>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={() => setEditingOrder(prev => ({ ...prev, items: [...(prev.items || []), { name: '', code: '', qty: 1, unitPrice: 0 }] }))}>Agregar ítem</Button>
              </Grid>
              {editOrderType === 'service' && editingOrder.totals && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Totales</Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={4}><TextField fullWidth size="small" label="Subtotal" type="number" value={editingOrder.totals?.subtotal || ''} onChange={e => setEditingOrder(prev => ({ ...prev, totals: { ...prev.totals, subtotal: Number(e.target.value) || 0 } }))} /></Grid>
                    <Grid item xs={4}><TextField fullWidth size="small" label="Transporte" type="number" value={editingOrder.totals?.transport || ''} onChange={e => setEditingOrder(prev => ({ ...prev, totals: { ...prev.totals, transport: Number(e.target.value) || 0 } }))} /></Grid>
                    <Grid item xs={4}><TextField fullWidth size="small" label="Total" type="number" value={editingOrder.totals?.total || ''} onChange={e => setEditingOrder(prev => ({ ...prev, totals: { ...prev.totals, total: Number(e.target.value) || 0 } }))} /></Grid>
                  </Grid>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Observaciones" value={editingOrder.notes || editingOrder.observaciones || ''} onChange={e => setEditingOrder(prev => ({ ...prev, notes: e.target.value, observaciones: e.target.value }))} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingOrder(null)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveOrder}>Guardar Cambios</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={2500} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
   
    {/* --- DIÁLOGO PARA SELECCIONAR RANGO Y TIPO DE REPORTE PDF --- */}
      <Dialog open={openPdfDialog} onClose={() => setOpenPdfDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generar Reporte PDF</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Configure los filtros para la Hoja de Ruta:
            </Typography>

            {/* SELECTOR DE TIPO DE REPORTE */}
            <FormControl fullWidth>
              <InputLabel>¿Qué desea imprimir?</InputLabel>
              <Select
                value={pdfReportType}
                label="¿Qué desea imprimir?"
                onChange={(e) => setPdfReportType(e.target.value)}
              >
                <MenuItem value="all">Todo (Eventos + Logística)</MenuItem>
                <MenuItem value="events">Solo Eventos (Comercial)</MenuItem>
                <MenuItem value="logistics">Solo Logística (Operativo)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Fecha Inicio"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={pdfDateRange.start}
              onChange={(e) => setPdfDateRange({ ...pdfDateRange, start: e.target.value })}
              fullWidth
            />
            <TextField
              label="Fecha Fin"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={pdfDateRange.end}
              onChange={(e) => setPdfDateRange({ ...pdfDateRange, end: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPdfDialog(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            color="secondary" 
            startIcon={<PdfIcon />} 
            onClick={exportCalendarSchedule}
          >
            Descargar
          </Button>
        </DialogActions>
      </Dialog>
       </ThemeProvider>
  );
}